import type {
  PerformedTreatment,
  PortalOverview,
  AllergyCategory,
  AllergySeverity,
  AllergyStatus,
  Appointment,
  AppointmentConsent,
  AppointmentStatus,
  AppointmentWithPatient,
  AssistedEncounterProposal,
  PlatformAssistantResponse,
  AuditEvent,
  CampaignReturn,
  CashMovement,
  CashSession,
  CashSessionDetail,
  ClinicalEncounter,
  ClinicalFormAssignment,
  ClinicalFormResponse,
  ClinicalFormTemplate,
  ClinicalInstruction,
  ClinicalDocumentRecord,
  ClinicalDocumentTemplate,
  ClinicalDocumentType,
  ClinicalOrder,
  ClinicalOrderResult,
  ClinicalOrderWithResults,
  ClinicalProfileEvent,
  ClinicalProfileSummary,
  ClinicalSource,
  ClinicalTask,
  ClinicalTaskEvent,
  ConsentDocument,
  ConsentKind,
  ConsultationNote,
  ConsultationNoteWithAppointment,
  Currency,
  DashboardSummary,
  DeletionRequest,
  EncounterDiagnosis,
  FinancialCategory,
  FinancialEntry,
  InboxOrder,
  InventoryItem,
  InventoryLotMovement,
  InventoryLotSummary,
  InboxConversation,
  InboxMetrics,
  InboxStatus,
  FamilyContact,
  FamilyRelationshipType,
  Location,
  MarketingConsent,
  MedicationStatus,
  OrderType,
  Organization,
  OrganizationMember,
  OrganizationRole,
  OrganizationStatus,
  Patient,
  PatientRelationship,
  RelationshipScope,
  PatientAllergy,
  PatientMedication,
  PatientPhoto,
  PatientProblem,
  PatientSpend,
  PatientVital,
  PayoutDeduction,
  PayoutDetail,
  PayoutScheme,
  PhotoKind,
  ProblemCodeSystem,
  ProblemEvent,
  ProblemSeverity,
  ProblemStatus,
  ProblemSummary,
  Professional,
  Prescription,
  PrescriptionConnection,
  ProfessionalActivity,
  ProfessionalCapacityReport,
  ProfessionalCredential,
  ProfessionalDetail,
  ProfessionalMetricsReport,
  PreconsultationAnswer,
  PreconsultationBundle,
  PreconsultationField,
  PreconsultationSubmission,
  PreconsultationTemplate,
  PreconsultationTemplateVersion,
  ProfessionalPayout,
  PurgeJob,
  QueueBoard,
  QueueDisplay,
  QueueEntry,
  QueueEvent,
  QueueMetricsReport,
  ResultSource,
  RetentionImpact,
  RetentionPolicy,
  ScheduleResource,
  SecurityPolicy,
  Specialty,
  SedePanel,
  SurveyResponse,
  TaskCounts,
  TaskFilters,
  TaskPriority,
  TaskQueue,
  TaskRow,
  TaskStatus,
  TaskType,
  TimelineCategory,
  TimelinePage,
  TimelineSummary,
  Treatment,
  TreatmentBudget,
  TreatmentBudgetDraft,
  TreatmentBudgetVersion,
  Referral,
  ReferralContact,
  ReferralMetrics,
  ReferralStatus,
  VisitPriority,
  VisitState,
  VitalSeries,
  VitalType,
  VitalsSummary,
} from "../types.ts";
import { getToken, signOut } from "./auth.ts";
import type { AccountRole } from "./auth-role.ts";
import { API_URL } from "./config.ts";
import { translateErrorMessage } from "./error-message.ts";

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Per-field messages from a 400, keyed by field name. */
  readonly fields: Record<string, string>;

  constructor(
    message: string,
    status: number,
    fields: Record<string, string> = {},
    code = "error",
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.fields = fields;
    this.code = code;
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string; fields?: Record<string, string> };
}

async function req<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...(opts.headers as Record<string, string> | undefined),
  };

  const token = getToken();
  if (token) headers.authorization = `Bearer ${token}`;
  const organizationId = localStorage.getItem("dd-organization-id");
  if (organizationId) headers["x-organization-id"] = organizationId;
  // Only declare a JSON body when one is actually sent — Fastify rejects a
  // bodyless request that claims content-type: application/json.
  if (opts.body && !headers["content-type"])
    headers["content-type"] = "application/json";

  const res = await fetch(`${API_URL}/api${path}`, { ...opts, headers });

  if (res.status === 401) {
    signOut();
    if (window.rondaDesktop) {
      if (location.hash !== "#/login") location.hash = "#/login";
    } else if (location.pathname !== "/login") {
      location.href = "/login";
    }
    throw new ApiError("Tu sesión venció. Volvé a ingresar.", 401);
  }

  if (res.status === 204) return undefined as T;

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    const body = data as ErrorBody | null;
    const error = new ApiError(
      translateErrorMessage(
        body?.error?.message ?? res.statusText,
        "No pudimos completar la solicitud. Intentá nuevamente.",
      ),
      res.status,
      Object.fromEntries(
        Object.entries(body?.error?.fields ?? {}).map(([field, message]) => [
          field,
          translateErrorMessage(message, "Revisá este campo."),
        ]),
      ),
      body?.error?.code,
    );
    if (res.status === 428)
      window.dispatchEvent(new CustomEvent("dd:step-up-required"));
    if (res.status === 402)
      window.dispatchEvent(new CustomEvent("dd:subscription-required"));
    throw error;
  }

  return data as T;
}

async function download(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.authorization = `Bearer ${token}`;
  const organizationId = localStorage.getItem("dd-organization-id");
  if (organizationId) headers["x-organization-id"] = organizationId;
  const response = await fetch(`${API_URL}/api${path}`, { headers });
  if (!response.ok)
    throw new ApiError("No pudimos descargar el reporte.", response.status);
  return response.blob();
}

const json = (body: unknown) => JSON.stringify(body);

export interface PatientInput {
  firstName: string;
  lastName: string;
  email?: string | null;
  phone?: string | null;
  documentId?: string | null;
  birthDate?: string | null;
  notes?: string | null;
  conditions?: string | null;
  allergies?: string | null;
  medications?: string | null;
  contraindications?: string | null;
  source?: string | null;
  marketingConsent?: MarketingConsent;
  status?: "active" | "inactive" | null;
  consentSigned?: boolean | null;
  nextTreatment?: string | null;
  nextTreatmentAt?: string | null;
  tags?: string | null;
  campaignId?: string | null;
}

export interface AppointmentInput {
  patientId: string;
  startsAt: number;
  durationMin?: number;
  reason?: string | null;
  treatmentId?: string | null;
  additionalTreatments?: Array<{
    treatmentId: string | null;
    name: string;
  }>;
  appointmentTypeId?: string | null;
  resourceIds?: string[];
  professionalId?: string | null;
  /** En qué sede se atiende. */
  locationId?: string | null;
  modality?: "onsite" | "remote";
  confirmation?: "pending" | "confirmed";
  depositAmount?: number;
  allDay?: boolean;
  consent?: AppointmentConsent;
  overbook?: boolean;
  overbookReason?: string | null;
  recurrence?: {
    frequency: "weekly" | "monthly";
    interval: number;
    occurrenceCount: number;
    timezone: string;
  } | null;
}

export interface ScheduleWaitlistEntryInput {
  patientId: string;
  professionalId?: string | null;
  locationId?: string | null;
  preferredWeekdays?: number[];
  windowStartMinute?: number;
  windowEndMinute?: number;
  durationMin?: number;
  minNoticeMinutes?: number;
  channel: "email" | "whatsapp" | "sms";
  priority?: number;
}

export interface ScheduleWaitlistEntry {
  entry: ScheduleWaitlistEntryInput & {
    id: string;
    status: "active" | "paused" | "fulfilled" | "left";
    createdAt: number;
  };
  patient: Patient;
}

export interface AuditFilters {
  before?: number;
  beforeId?: string;
  from?: number;
  to?: number;
  actorId?: string;
  action?: string;
  resource?: string;
  resourceId?: string;
  outcome?: "success" | "denied" | "failure";
  limit?: number;
}

export interface CommunicationDelivery {
  id: string;
  status:
    | "ready_for_provider"
    | "queued"
    | "sent"
    | "delivered"
    | "read"
    | "failed"
    | "bounced"
    | "complained"
    | "opted_out";
  channel: "email" | "whatsapp" | "sms";
  recipient: string | null;
  subject: string | null;
  scheduledAt: number;
  sentAt: number | null;
  deliveredAt: number | null;
  error: string | null;
  attempts: number;
}
export type CommunicationChannel = "email" | "whatsapp" | "sms";
export type CommunicationEventType =
  | "appointment.created"
  | "appointment.reminder"
  | "appointment.changed"
  | "appointment.cancelled"
  | "form.requested"
  | "survey.requested"
  | "treatment.renewal"
  | "campaign.promotional"
  | "document.ready"
  | "follow_up.due"
  | "waitlist.offer";
export interface CommunicationTemplate {
  id: string;
  name: string;
  eventType: CommunicationEventType;
  channel: CommunicationChannel;
  locale: string;
  version: number;
  subject: string | null;
  body: string;
  allowedVariables: string[];
  enabled: boolean;
  createdAt: number;
  updatedAt: number;
}
export interface CommunicationRule {
  id: string;
  name: string;
  eventType: CommunicationEventType;
  channel: CommunicationChannel;
  templateId: string;
  delayMinutes: number;
  enabled: boolean;
  createdAt: number;
  updatedAt: number;
}
export interface CalendarConnection {
  id: string;
  professionalId: string;
  provider: "google" | "microsoft";
  accountLabel: string | null;
  status: "active" | "reauthorization_required" | "error";
  lastSyncedAt: number | null;
  lastErrorCode: string | null;
  consecutiveFailures: number;
  calendars: Array<{
    id: string;
    name: string;
    blocksAvailability: boolean;
    publishesAppointments: boolean;
    lastSyncedAt: number | null;
  }>;
  conflicts: Array<{ appointmentId: string; reason: string | null }>;
}

export interface ProviderCalendar {
  id: string;
  name: string;
  primary: boolean;
}
export type PatientImportField =
  | "firstName"
  | "lastName"
  | "email"
  | "phone"
  | "documentId"
  | "birthDate"
  | "externalId"
  | "source"
  | "tags";
export interface PatientImportRow {
  id: string;
  rowNumber: number;
  normalized: Partial<Record<PatientImportField, string | null>>;
  errors: Array<{
    column: string;
    value: string | null;
    code: string;
    message: string;
  }>;
  candidates: Array<{
    patientId: string;
    name: string;
    score: number;
    reasons: string[];
  }>;
  decision: "create" | "skip" | "merge" | null;
  targetPatientId: string | null;
}
export interface PatientImportJob {
  id: string;
  filename: string;
  format: "csv" | "xlsx";
  status: string;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  duplicateRows: number;
  importedRows: number;
}

export interface AvailabilityRule {
  id: string;
  professionalId: string;
  locationId: string | null;
  modality: "onsite" | "remote";
  weekday: number;
  startMinute: number;
  endMinute: number;
  validFrom: string;
  validTo: string | null;
  priority: number;
}
export interface AvailabilityException {
  id: string;
  professionalId: string;
  locationId: string | null;
  modality: "onsite" | "remote" | null;
  kind: "available" | "unavailable";
  startsAt: number;
  endsAt: number;
  reason: string;
  priority: number;
}
/**
 * La política de la agenda pública de la clínica.
 *
 * Todo en minutos y días, igual que del lado del servidor: la pantalla convierte
 * a "2 horas" o "un mes" al mostrarlo, y guardar el texto formateado sería
 * volver a parsearlo en cada lectura.
 */
export interface BookingSettings {
  minNoticeMin: number;
  maxHorizonDays: number;
  depositPolicy: "none" | "optional" | "required";
  /** En la unidad mínima de la moneda de la clínica. */
  depositAmount: number;
  holdMinutes: number;
  cancellationCutoffMin: number;
  allowReschedule: boolean;
  termsVersion: string;
  termsText: string | null;
}

/** Una combinación reservable desde la web: quién, para qué y dónde. */
export interface BookingOffering {
  id: string;
  ownerId: string;
  professionalId: string;
  locationId: string | null;
  modality: "onsite" | "remote";
  appointmentTypeId: string | null;
  treatmentId: string | null;
  publicName: string;
  description: string | null;
  durationMin: number;
  /** Nulo usa la seña general de la clínica. */
  depositAmount: number | null;
  priceAmount: number | null;
  active: boolean;
  sortOrder: number;
}

/** El micrositio público de la clínica. */
export interface ClinicSite {
  id: string;
  ownerId: string;
  slug: string;
  status: "draft" | "published" | "unpublished";
  publishedAt: number | null;
  unpublishedAt: number | null;
  headline: string | null;
  subheadline: string | null;
  description: string | null;
  primaryCtaLabel: string | null;
  whatsappPhone: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  sections: SiteSection[];
  faq: Array<{ question: string; answer: string }>;
  coverages: string[];
  gallery: Array<{ url: string; alt: string }>;
  paletteId: string | null;
  cacheTtlSeconds: number;
}

/** Las secciones de la plantilla. Lista cerrada: la plantilla es opinionada. */
export const SITE_SECTIONS = [
  "hero",
  "services",
  "professionals",
  "locations",
  "hours",
  "reviews",
  "coverages",
  "gallery",
  "faq",
  "contact",
] as const;
export type SiteSection = (typeof SITE_SECTIONS)[number];

export interface ClinicSiteView {
  site: ClinicSite;
  /** La URL pública ya armada, para copiarla de un tirón. */
  url: string;
}

export const PROFILE_FIELDS = [
  "photo",
  "bio",
  "pronouns",
  "specialties",
  "languages",
  "license",
  "locations",
  "services",
  "availability",
  "rating",
] as const;
export type ProfileField = (typeof PROFILE_FIELDS)[number];

/** La publicación web de una persona; la ficha interna sigue siendo la fuente. */
export interface ProfessionalProfile {
  id: string;
  ownerId: string;
  professionalId: string;
  slug: string;
  status: "draft" | "published" | "unpublished";
  publishedAt: number | null;
  unpublishedAt: number | null;
  displayName: string | null;
  photoUrl: string | null;
  pronouns: string | null;
  bio: string | null;
  languages: string[];
  publicLicense: string | null;
  visibleFields: ProfileField[];
  ratingMinSample: number;
  sortOrder: number;
  listed: boolean;
  createdAt: number;
  updatedAt: number;
}

export type ProfessionalProfileInput = Pick<
  ProfessionalProfile,
  | "displayName"
  | "photoUrl"
  | "pronouns"
  | "bio"
  | "languages"
  | "publicLicense"
  | "visibleFields"
  | "ratingMinSample"
  | "sortOrder"
  | "listed"
>;

/** Si el nombre está libre, y con qué reemplazarlo si no. */
export interface SlugCheck {
  slug: string;
  available: boolean;
  reason: string | null;
  suggestions: string[];
}

/** Conteos del micrositio, agregados por día y campaña. */
export interface SiteVisitDay {
  day: string;
  surface: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  views: number;
  bookingClicks: number;
  whatsappClicks: number;
}

/**
 * La configuración de la agenda pública y si está encendida.
 *
 * `enabledAt` es una fecha y no un booleano por lo mismo que del lado del
 * servidor: "está apagada" y "nunca se encendió" se distinguen mirándola.
 */
export interface BookingConfig {
  settings: BookingSettings;
  enabledAt: number | null;
}

/** Qué falta para poder publicar la agenda. */
export interface BookingReadiness {
  ready: boolean;
  blockers: Array<{ id: string; title: string; action: string }>;
}

/** Un turno que entró por la web, para revisarlo desde el panel. */
export interface PublicBookingRequest {
  id: string;
  appointmentId: string;
  offeringId: string | null;
  patientId: string;
  patientCreated: boolean;
  /** Campos cuyo valor enviado no coincide con la ficha. Nulo: coincide todo. */
  contactMismatch: string | null;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string | null;
  contactPhone: string | null;
  contactDocumentId: string | null;
  reason: string | null;
  source: string;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  depositStatus: "not_required" | "pending" | "paid" | "expired" | "refunded";
  depositAmount: number;
  cancelledAt: number | null;
  createdAt: number;
}

export interface LedgerTransaction {
  id: string;
  patientId: string | null;
  /** Dónde entró la plata. */
  locationId: string | null;
  /** Dónde se atendió, cuando no es la misma. Nulo: la de cobro. */
  serviceLocationId: string | null;
  /** La caja abierta contra la que se cobró. */
  cashSessionId: string | null;
  appointmentId: string | null;
  kind:
    | "charge"
    | "payment"
    | "adjustment"
    | "refund"
    | "reversal"
    | "exchange_in"
    | "exchange_out";
  direction: "debit" | "credit";
  amount: number;
  currency: string;
  paymentMethod:
    | "cash"
    | "bank_transfer"
    | "debit_card"
    | "credit_card"
    | "insurance"
    | "other"
    | null;
  description: string;
  receiptNumber: string | null;
  reversesTransactionId: string | null;
  occurredAt: number;
  createdAt: number;
  appliedAmount: number;
  remainingAmount: number | null;
}
export interface LedgerAccount {
  transactions: LedgerTransaction[];
  applications: Array<{
    id: string;
    paymentTransactionId: string;
    chargeTransactionId: string;
    amount: number;
    kind: "apply" | "unapply";
  }>;
  balances: Record<string, number>;
}

export interface ReceiptConfiguration {
  id: string;
  locationId: string | null;
  scopeKey: string;
  currency: string;
  prefix: string;
  nextNumber: number;
  updatedAt: number;
}

/** Una consulta que ya pasó, cargada desde el historial del paciente. */
export interface RecordVisitInput {
  patientId: string;
  /** Epoch ms. Hoy o antes. */
  startsAt: number;
  reason?: string | null;
  professionalId?: string | null;
  locationId?: string | null;
}

export type AppointmentUpdate = Partial<Omit<AppointmentInput, "patientId">> & {
  status?: AppointmentStatus;
};

export interface NoteInput {
  body: string;
  /** La consulta de la que sale la nota, o null si es una nota de la ficha. */
  appointmentId?: string | null;
  author?: string | null;
  /** El archivo que la acompaña, si hay uno. `data` va en base64 sin prefijo. */
  attachment?: { name: string; mimeType: string; data: string } | null;
}

/** Un movimiento del libro, tal como se carga. */
export interface FinanceInput {
  type: "income" | "expense";
  categoryId: string;
  patientId?: string | null;
  appointmentId?: string | null;
  /** El tratamiento cobrado, cuando no sale de una consulta. */
  treatment?: string | null;
  /**
   * En qué sede. Vacío es legítimo —el alquiler no es de ninguna— y con una
   * consulta elegida lo resuelve el API a partir de ella.
   */
  locationId?: string | null;
  description: string;
  amount: number;
  /** En qué moneda entró. Por defecto, pesos. */
  currency?: Currency;
  occurredAt: number;
}

/** Una campaña, tal como se carga. */
export interface CampaignInput {
  name: string;
  channel: string;
  spend: number;
  /** En qué moneda se pagó. Por defecto, pesos. */
  spendCurrency?: Currency;
  spentAt?: number;
  enquiries: number;
}

export interface ListAppointmentsParams {
  from?: number;
  to?: number;
  patientId?: string;
  resourceId?: string;
  /** La sede del selector. El servidor la cruza con lo que la membresía permite. */
  locationId?: string;
  limit?: number;
  order?: "asc" | "desc";
}

type ClinicalUploadType =
  "photo" | "consent" | "questionnaire" | "relationship";

interface ClinicalUploadSession {
  upload: {
    id: string;
    status: string;
    rejectionReason: string | null;
  };
  uploadUrl: string;
  uploadMethod: "PUT";
  requiredHeaders: Record<string, string>;
  requiresApiAuthorization: boolean;
}

async function uploadClinicalFile(
  patientId: string,
  resourceType: ClinicalUploadType,
  file: Blob,
  mimeType: string,
): Promise<string> {
  const session = await req<ClinicalUploadSession>("/storage/uploads", {
    method: "POST",
    body: json({
      patientId,
      resourceType,
      mimeType,
      byteSize: file.size,
    }),
  });
  const headers = { ...session.requiredHeaders };
  if (session.requiresApiAuthorization) {
    const token = getToken();
    const organizationId = localStorage.getItem("dd-organization-id");
    if (token) headers.authorization = `Bearer ${token}`;
    if (organizationId) headers["x-organization-id"] = organizationId;
  }
  const uploaded = await fetch(session.uploadUrl, {
    method: session.uploadMethod,
    headers,
    body: file,
  });
  if (!uploaded.ok) {
    throw new ApiError("No pudimos transferir el archivo.", uploaded.status);
  }
  const completed = await req<{
    upload: { id: string; status: string; rejectionReason: string | null };
  }>(`/storage/uploads/${session.upload.id}/complete`, { method: "POST" });
  if (completed.upload.status !== "ready") {
    throw new ApiError(
      "El archivo no superó la verificación de seguridad.",
      422,
    );
  }
  return completed.upload.id;
}

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export interface ActionableNotification {
  id: string;
  kind: string;
  category: "clinical" | "operational" | "commercial";
  severity: "info" | "warning" | "critical";
  title: string;
  detail: string;
  resourceType: string;
  resourceId: string;
  href: string;
  dueAt: number | null;
  firstSeenAt: number;
  readAt: number | null;
  resolvedAt: number | null;
}

export interface NotificationCenter {
  notifications: ActionableNotification[];
  metrics: {
    active: number;
    unread: number;
    resolved: number;
    averageResolutionMinutes: number | null;
  };
}

/**
 * Un archivo servido por la API, con la sesión puesta.
 *
 * No pasa por `req` porque la respuesta no es JSON. Las fotos clínicas y los
 * consentimientos viven fuera del árbol público justamente para que haga falta
 * esto: sin cabecera de sesión y sin organización activa, el servidor no los
 * entrega.
 */
async function fileBlob(
  path: string,
  whenItFails: string,
  requestedOrganizationId?: string,
): Promise<Blob> {
  const token = getToken();
  const organizationId =
    requestedOrganizationId ?? localStorage.getItem("dd-organization-id");
  const res = await fetch(`${API_URL}/api${path}`, {
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(organizationId ? { "x-organization-id": organizationId } : {}),
    },
  });
  if (!res.ok) throw new ApiError(whenItFails, res.status);
  return res.blob();
}

async function clinicalFileBlob(path: string, whenItFails: string) {
  const signed = await req<{ url: string | null }>(`${path}/download-url`);
  if (!signed.url) return fileBlob(`${path}/file`, whenItFails);
  const response = await fetch(signed.url, { credentials: "omit" });
  if (!response.ok) throw new ApiError(whenItFails, response.status);
  return response.blob();
}

export const api = {
  subscription: {
    status: () =>
      req<{
        configured: boolean;
        plans: Array<{
          key: "consultorio" | "clinica" | "centro";
          name: string;
          includedProfessionals: number;
          monthlyBase: number;
          annualBase: number;
          monthlyAdditional: number | null;
          annualAdditional: number | null;
        }>;
        promotionEligible: boolean;
        trial: {
          startedAt: number | null;
          endsAt: number | null;
          active: boolean;
        };
        subscription: null | {
          status: string;
          stripePriceId: string;
          currentPeriodEnd: number | null;
          cancelAtPeriodEnd: boolean;
        };
        activeProfessionals: Array<{ id: string; name: string }>;
      }>("/subscription"),
    checkout: (
      plan: "consultorio" | "clinica" | "centro",
      cycle: "monthly" | "annual",
    ) =>
      req<{ url: string }>("/subscription/checkout", {
        method: "POST",
        body: json({ plan, cycle }),
      }),
    portal: () =>
      req<{ url: string }>("/subscription/portal", { method: "POST" }),
    deactivateProfessional: (id: string) =>
      req<void>(`/subscription/professionals/${id}`, { method: "DELETE" }),
  },
  notifications: {
    list: () => req<NotificationCenter>("/notifications"),
    transition: (id: string, action: "read" | "resolve" | "reopen") =>
      req(`/notifications/${id}`, {
        method: "PATCH",
        body: json({ action }),
      }),
    markAllRead: () =>
      req<{ updated: number }>("/notifications/read-all", { method: "POST" }),
  },
  /**
   * El panel de pacientes, de una sola vez.
   *
   * Fichas, turnos y la visita de hoy vienen juntos porque la pantalla de
   * inicio los muestra a la vez: partirlo en tres pedidos deja tres estados de
   * carga distintos en una pantalla que se lee de un vistazo.
   */
  portal: {
    overview: () => req<PortalOverview>("/portal/overview"),
    confirmLink: (linkId: string) =>
      req<void>(`/portal/links/${linkId}/confirm`, { method: "POST" }),
    revokeLink: (linkId: string) =>
      req<void>(`/portal/links/${linkId}/revoke`, { method: "POST" }),
    /** Qué dice la invitación, antes de tener sesión. Público. */
    inviteePreview: (token: string) =>
      req<{ invite: { clinic: string; firstName: string } }>(
        `/public/portal-invite/${encodeURIComponent(token)}`,
      ).then((r) => r.invite),
    acceptInvite: (token: string) =>
      req<void>("/portal/invites/accept", {
        method: "POST",
        body: JSON.stringify({ token }),
      }),
    /** "Llegué": el check-in desde adentro de la sesión. */
    arrive: (visitId: string) =>
      req<{ status: string; arrivedAt: number }>(
        `/portal/visits/${visitId}/arrive`,
        { method: "POST" },
      ),
    stepUp: (
      patientId: string,
      input: { password?: string; code?: string; recovery?: boolean },
    ) =>
      req<{ grant: { expiresAt: number } }>(
        `/portal/step-up/${encodeURIComponent(patientId)}`,
        { method: "POST", body: json(input) },
      ),
    documentFile: (documentId: string) =>
      fileBlob(
        `/portal/documents/${encodeURIComponent(documentId)}/file`,
        "No pudimos descargar el documento.",
      ),
  },

  documents: {
    templates: () =>
      req<{ templates: ClinicalDocumentTemplate[] }>(
        "/document-templates",
      ).then((result) => result.templates),
    createTemplate: (input: {
      type: ClinicalDocumentType;
      name: string;
      blocks: ClinicalDocumentTemplate["blocks"];
    }) =>
      req<{ template: ClinicalDocumentTemplate }>("/document-templates", {
        method: "POST",
        body: json(input),
      }).then((result) => result.template),
    submitTemplate: (id: string) =>
      req<{ template: ClinicalDocumentTemplate }>(
        `/document-templates/${id}/submit`,
        { method: "POST" },
      ).then((result) => result.template),
    approveTemplate: (id: string) =>
      req<{ template: ClinicalDocumentTemplate }>(
        `/document-templates/${id}/approve`,
        { method: "POST" },
      ).then((result) => result.template),
    forPatient: (patientId: string) =>
      req<{ documents: ClinicalDocumentRecord[] }>(
        `/patients/${patientId}/documents`,
      ).then((result) => result.documents),
    preview: (input: {
      patientId: string;
      templateId: string;
      professionalId: string;
      city?: string | null;
    }) =>
      req<{
        snapshot: { blocks: ClinicalDocumentTemplate["blocks"] };
        missing: string[];
      }>("/documents/preview", { method: "POST", body: json(input) }),
    issue: (input: {
      patientId: string;
      templateId: string;
      professionalId: string;
      city?: string | null;
      acceptMissing?: boolean;
      replacesId?: string | null;
    }) =>
      req<{
        document: ClinicalDocumentRecord;
        verificationUrl: string;
        missing: string[];
      }>("/documents", { method: "POST", body: json(input) }),
    void: (id: string, reason: string) =>
      req<{ document: ClinicalDocumentRecord }>(`/documents/${id}/void`, {
        method: "POST",
        body: json({ reason }),
      }).then((result) => result.document),
    file: (id: string) =>
      fileBlob(
        `/documents/${encodeURIComponent(id)}/file`,
        "No pudimos descargar el documento.",
      ),
  },

  /**
   * Invitar a una paciente a su portal.
   *
   * Le manda un correo con un enlace propio. Ese clic reemplaza a la
   * verificación de correo que antes trababa el enganche: llega a la casilla
   * que la ficha tiene cargada, así que abrirlo prueba lo mismo.
   */
  /**
   * Los tratamientos realizados de una paciente, con lo que consumieron.
   *
   * Cargar uno descuenta sus insumos del inventario en la misma operación, y
   * borrarlo los devuelve: el descuento manual era lo que hacía que el stock
   * dejara de coincidir con la realidad a la semana.
   */
  performedTreatments: {
    forPatient: (patientId: string) =>
      req<{ treatments: PerformedTreatment[] }>(
        `/patients/${patientId}/treatments`,
      ).then((r) => r.treatments),
    record: (input: {
      patientId: string;
      treatmentId?: string | null;
      treatmentName: string;
      encounterId?: string | null;
      appointmentId?: string | null;
      professionalId?: string | null;
      performedAt: number;
      notes?: string | null;
      supplies: { itemId: string; quantity: number }[];
    }) =>
      req<{ treatment: PerformedTreatment }>("/performed-treatments", {
        method: "POST",
        body: json(input),
      }).then((r) => r.treatment),
    remove: (id: string) =>
      req<void>(`/performed-treatments/${id}`, { method: "DELETE" }),
  },

  invitePatientToPortal: (patientId: string) =>
    req<{
      invite: {
        email: string;
        expiresAt: number;
        /** Para mandarlo por WhatsApp, o cuando el correo no salió. */
        url: string;
        /** Si el correo efectivamente salió. La invitación vale igual. */
        sent: boolean;
      };
    }>(`/patients/${patientId}/portal-invite`, { method: "POST" }).then(
      (r) => r.invite,
    ),

  /**
   * Fija el rol de una cuenta recién creada, si todavía no tiene uno.
   *
   * Sólo hace falta para el alta con Google: ahí no hay formulario donde
   * mandarlo junto con la cuenta. La API sólo escribe si la columna está en
   * nulo, así que entrar con Google a una cuenta que ya existe no la reescribe
   * —y repetir el pedido tampoco—.
   */
  claimAccountRole: (role: AccountRole) =>
    req<{ role: AccountRole }>("/account/role", {
      method: "POST",
      body: JSON.stringify({ role }),
    }).then((response) => response.role),

  /**
   * Qué formas de ingresar ofrece esta API.
   *
   * Se pregunta sin sesión —quien mira la pantalla de ingreso todavía no la
   * tiene— y decide si se dibuja "Continuar con Google". Va por la red y no
   * por una variable de compilación para que el panel y la API no puedan
   * quedar en desacuerdo: si el proveedor no está configurado del otro lado,
   * el botón no aparece de este.
   */
  authProviders: () =>
    req<{ providers: { google: boolean } }>("/public/auth-providers").then(
      (response) => response.providers,
    ),
  security: {
    policy: () =>
      req<{ policy: SecurityPolicy }>("/security/policy").then(
        (response) => response.policy,
      ),
    updatePolicy: (
      input: Pick<
        SecurityPolicy,
        | "sessionLifetimeHours"
        | "inactivityHours"
        | "stepUpMinutes"
        | "requireMfa"
      >,
    ) =>
      req<{ policy: SecurityPolicy }>("/security/policy", {
        method: "PATCH",
        body: json(input),
      }).then((response) => response.policy),
    stepUp: (input: { password?: string; code?: string; recovery?: boolean }) =>
      req<{ grant: { expiresAt: number } }>("/security/step-up", {
        method: "POST",
        body: json(input),
      }).then((response) => response.grant),
  },
  clinicalUploads: {
    upload: uploadClinicalFile,
  },
  family: {
    list: (patientId: string) =>
      req<{
        relationships: Array<{
          relationship: PatientRelationship;
          contact: FamilyContact;
        }>;
        contacts: FamilyContact[];
      }>(`/patients/${patientId}/relationships`),
    create: (
      patientId: string,
      input: {
        contactId?: string;
        contact?: {
          firstName: string;
          lastName: string;
          email?: string | null;
          phone?: string | null;
          documentId?: string | null;
          linkedPatientId?: string | null;
        };
        relationship: {
          type: FamilyRelationshipType;
          scopes: RelationshipScope[];
          legalCapacity?: string | null;
          evidenceUploadId?: string | null;
          validFrom?: number;
          validUntil?: number | null;
          preferredForCommunication?: boolean;
          preferredForBilling?: boolean;
        };
      },
    ) =>
      req<{ relationship: PatientRelationship }>(
        `/patients/${patientId}/relationships`,
        { method: "POST", body: json(input) },
      ).then((result) => result.relationship),
    update: (
      id: string,
      input: Partial<{
        type: FamilyRelationshipType;
        scopes: RelationshipScope[];
        status: "pending" | "verified" | "rejected";
        legalCapacity: string | null;
        validFrom: number;
        validUntil: number | null;
        preferredForCommunication: boolean;
        preferredForBilling: boolean;
        reason: string;
      }> & { version: number },
    ) =>
      req<{ relationship: PatientRelationship }>(`/relationships/${id}`, {
        method: "PATCH",
        body: json(input),
      }).then((result) => result.relationship),
    revoke: (id: string, version: number, reason: string) =>
      req<{ relationship: PatientRelationship }>(
        `/relationships/${id}/revoke`,
        { method: "POST", body: json({ version, reason }) },
      ).then((result) => result.relationship),
    evidence: (id: string) =>
      clinicalFileBlob(
        `/relationships/${id}/evidence`,
        "No pudimos abrir la evidencia.",
      ),
  },
  inventory: {
    list: () =>
      req<{ items: InventoryItem[] }>("/inventory").then((r) => r.items),
    create: (input: Omit<InventoryItem, "id" | "updatedAt">) =>
      req<{ item: InventoryItem }>("/inventory", {
        method: "POST",
        body: json(input),
      }).then((r) => r.item),
    update: (
      id: string,
      input: Partial<Omit<InventoryItem, "id" | "updatedAt">> & {
        adjustmentReason?: string;
      },
    ) =>
      req<{ item: InventoryItem }>(`/inventory/${id}`, {
        method: "PATCH",
        body: json(input),
      }).then((r) => r.item),
    remove: (id: string) => req<void>(`/inventory/${id}`, { method: "DELETE" }),
    lots: (id: string) =>
      req<{ lots: InventoryLotSummary[] }>(`/inventory/${id}/lots`).then(
        (result) => result.lots,
      ),
    receive: (
      id: string,
      input: {
        lotNumber: string;
        reference: string;
        supplier?: string | null;
        locationId?: string | null;
        expiresAt?: number | null;
        quantity: number;
        unit: string;
      },
    ) =>
      req(`/inventory/${id}/lots`, {
        method: "POST",
        body: json(input),
      }),
    move: (input: {
      lotId: string;
      type: "consumption" | "adjustment" | "discard" | "return";
      quantity: number;
      direction?: "in" | "out";
      unit: string;
      reason?: string | null;
      expiredOverrideReason?: string | null;
    }) =>
      req("/inventory/movements", {
        method: "POST",
        body: json(input),
      }),
    transfer: (input: {
      lotId: string;
      toLocationId: string;
      quantity: number;
      unit: string;
      inTransit: boolean;
      reason?: string | null;
    }) =>
      req("/inventory/transfers", {
        method: "POST",
        body: json(input),
      }),
    recall: (lotId: string, reason: string) =>
      req(`/inventory/lots/${lotId}/recall`, {
        method: "POST",
        body: json({ reason }),
      }),
    trace: (itemId: string) =>
      req<{ movements: InventoryLotMovement[] }>(
        `/inventory-traceability?itemId=${encodeURIComponent(itemId)}`,
      ).then((result) => result.movements),
  },
  finances: {
    list: (locationId?: string) =>
      req<{ entries: FinancialEntry[] }>(
        `/finances${query({ locationId })}`,
      ).then((r) => r.entries),
    create: (input: FinanceInput) =>
      req<{ entry: FinancialEntry }>("/finances", {
        method: "POST",
        body: json(input),
      }).then((r) => r.entry),
    /**
     * Corregir un movimiento ya cargado, sin borrarlo y volver a crearlo: eso
     * reescribía la fecha de registro. El tipo no se puede cambiar.
     */
    update: (id: string, input: Partial<Omit<FinanceInput, "type">>) =>
      req<{ entry: FinancialEntry }>(`/finances/${id}`, {
        method: "PATCH",
        body: json(input),
      }).then((r) => r.entry),
    byPatient: (locationId?: string) =>
      req<{ spend: PatientSpend[] }>(
        `/finances/by-patient${query({ locationId })}`,
      ).then((r) => r.spend),
    remove: (id: string) => req<void>(`/finances/${id}`, { method: "DELETE" }),
    categories: () =>
      req<{ categories: FinancialCategory[] }>("/finance-categories").then(
        (r) => r.categories,
      ),
    createCategory: (input: { name: string; type: "income" | "expense" }) =>
      req<{ category: FinancialCategory }>("/finance-categories", {
        method: "POST",
        body: json(input),
      }).then((r) => r.category),
    removeCategory: (id: string) =>
      req<void>(`/finance-categories/${id}`, { method: "DELETE" }),
  },
  surveys: {
    list: (locationId?: string) =>
      req<{ responses: SurveyResponse[] }>(
        `/surveys${query({ locationId })}`,
      ).then((r) => r.responses),
    create: (input: {
      patientId?: string | null;
      appointmentId?: string | null;
      score: number;
      comment?: string | null;
    }) =>
      req<{ response: SurveyResponse }>("/surveys", {
        method: "POST",
        body: json(input),
      }).then((r) => r.response),
  },
  reviews: {
    form: () =>
      req<{
        form: {
          title: string;
          introduction: string;
          npsQuestion: string;
          ratingQuestion: string;
          commentLabel: string;
        };
      }>("/reviews/form").then((response) => response.form),
    saveForm: (input: {
      title: string;
      introduction: string;
      npsQuestion: string;
      ratingQuestion: string;
      commentLabel: string;
    }) =>
      req<{ form: unknown }>("/reviews/form", {
        method: "PUT",
        body: json(input),
      }),
    settings: () =>
      req<{
        settings: Array<{
          id: string;
          appointmentTypeId: string;
          enabled: boolean;
          delayHours: number;
          requestPublicReview: boolean;
          googleReviewUrl: string | null;
        }>;
        googleReviewUrl: string | null;
        appointmentTypes: Array<{
          id: string;
          name: string;
          durationMin: number;
          archivedAt: number | null;
        }>;
      }>("/reviews/settings"),
    saveSettings: (input: {
      appointmentTypeId: string;
      enabled: boolean;
      delayHours: number;
      requestPublicReview: boolean;
      googleReviewUrl: string | null;
    }) =>
      req<{ setting: unknown }>("/reviews/settings", {
        method: "PUT",
        body: json(input),
      }),
    saveGoogleSettings: (googleReviewUrl: string | null) =>
      req<{ googleReviewUrl: string | null }>("/reviews/google-settings", {
        method: "PUT",
        body: json({ googleReviewUrl }),
      }),
    createAppointmentType: (input: { name: string; durationMin: number }) =>
      req<{
        appointmentType: {
          id: string;
          name: string;
          durationMin: number;
          archivedAt: number | null;
        };
      }>("/reviews/appointment-types", {
        method: "POST",
        body: json(input),
      }).then((response) => response.appointmentType),
    invite: (appointmentId: string, notifyPatient = false) =>
      req<{ invitation: unknown; url: string }>(
        `/reviews/invitations/${appointmentId}`,
        {
          method: "POST",
          body: json({ notifyPatient }),
        },
      ),
    dashboard: (input: {
      from: number;
      to: number;
      professionalId?: string;
      locationId?: string;
    }) =>
      req<{
        summary: {
          average: number | null;
          sample: number;
          distribution: Record<"1" | "2" | "3" | "4" | "5", number>;
          period: { from: number; to: number };
        };
        responses: Array<{
          response: SurveyResponse;
          professionalName: string | null;
          locationName: string | null;
          recovery: null | {
            id: string;
            status: "open" | "contacted" | "resolved";
            assignedUserId: string | null;
            privateNotes: string | null;
          };
          review: null | {
            id: string;
            status: "pending" | "published" | "hidden" | "revoked";
            moderationReason: string | null;
          };
          replies: Array<{
            id: string;
            body: string;
            version: number;
            createdAt: number;
          }>;
        }>;
      }>(`/reviews/dashboard${query(input)}`),
    updateRecovery: (
      id: string,
      input: {
        status: "open" | "contacted" | "resolved";
        assignedUserId: string | null;
        privateNotes: string | null;
      },
    ) =>
      req<{ recovery: unknown }>(`/reviews/recovery/${id}`, {
        method: "PATCH",
        body: json(input),
      }),
    moderate: (
      id: string,
      input: {
        action: "publish" | "hide";
        reason: string | null;
      },
    ) =>
      req<{ review: unknown }>(`/reviews/public/${id}`, {
        method: "PATCH",
        body: json(input),
      }),
    reply: (id: string, body: string) =>
      req<{ reply: unknown }>(`/reviews/public/${id}/replies`, {
        method: "POST",
        body: json({ body }),
      }),
  },
  organization: {
    status: () =>
      req<OrganizationStatus>("/organization/status").then((status) => {
        if (status.organization) {
          localStorage.setItem("dd-organization-id", status.organization.id);
        } else {
          localStorage.removeItem("dd-organization-id");
        }
        return status;
      }),
    setup: (input: {
      name: string;
      employeeCount: number;
      primaryColor: string;
      accentColor: string;
      paletteId?: string | null;
      /** La moneda local. Por defecto, pesos argentinos. */
      currency?: Currency;
      invites: string[];
    }) =>
      req<{ organization: Organization }>("/organization/setup", {
        method: "POST",
        body: json(input),
      }).then((r) => {
        localStorage.setItem("dd-organization-id", r.organization.id);
        return r.organization;
      }),
    update: (
      input: Partial<
        Pick<
          Organization,
          | "name"
          | "employeeCount"
          | "primaryColor"
          | "accentColor"
          | "paletteId"
          | "currency"
          | "practiceSpecialty"
          | "opensAt"
          | "closesAt"
          | "timezone"
        >
      >,
    ) =>
      req<{ organization: Organization }>("/organization", {
        method: "PATCH",
        body: json(input),
      }).then((r) => r.organization),
    setLogo: (input: { mimeType: string; data: string }) =>
      req<{ organization: Organization }>("/organization/logo", {
        method: "POST",
        body: json(input),
      }).then((r) => r.organization),
    logo: (organizationId?: string) =>
      fileBlob(
        "/organization/logo",
        "No pudimos cargar el logo.",
        organizationId,
      ),
    removeLogo: () => req<void>("/organization/logo", { method: "DELETE" }),
    invite: (
      emails: string[],
      role: Exclude<OrganizationRole, "owner" | "member"> = "clinician",
    ) =>
      req<{ invitations: Organization["invitations"] }>(
        "/organization/invitations",
        {
          method: "POST",
          body: json({ emails, role }),
        },
      ).then((r) => r.invitations),
    revokeInvitation: (id: string) =>
      req<void>(`/organization/invitations/${id}`, { method: "DELETE" }),
    members: () =>
      req<{ members: OrganizationMember[] }>("/organization/members").then(
        (r) => r.members,
      ),
    updateMemberRole: (id: string, role: OrganizationRole) =>
      req<{ member: OrganizationMember }>(`/organization/members/${id}/role`, {
        method: "PATCH",
        body: json({ role }),
      }).then((r) => r.member),
    updateMemberAccess: (
      id: string,
      access: Pick<
        OrganizationMember,
        "role" | "professionalId" | "locationIds" | "patientScope"
      >,
    ) =>
      req<{ member: OrganizationMember }>(
        `/organization/members/${id}/access`,
        {
          method: "PATCH",
          body: json(access),
        },
      ).then((r) => r.member),
    /** Le quita el acceso a un miembro. La cuenta sigue existiendo. */
    removeMember: (id: string) =>
      req<void>(`/organization/members/${id}`, { method: "DELETE" }),
    exportData: () => req<Record<string, unknown>>("/organization/export"),
    remove: (confirmation: string) =>
      req<void>("/organization", {
        method: "DELETE",
        body: json({ confirmation }),
      }),
  },
  audit: {
    list: (filters: AuditFilters = {}) =>
      req<{ events: AuditEvent[] }>(
        `/audit-events${query({ ...filters })}`,
      ).then((r) => r.events),
  },
  communications: {
    patientConsents: (patientId: string) =>
      req<{
        consents: Array<{
          id: string;
          channel: CommunicationChannel;
          purpose: "survey" | "marketing" | "transactional";
          status: "pending" | "granted" | "revoked";
          updatedAt: number;
        }>;
      }>(`/patients/${patientId}/communication-consents`),
    setPatientConsent: (
      patientId: string,
      input: {
        channel: CommunicationChannel;
        purpose: "survey" | "marketing";
        granted: boolean;
        source: "staff";
        evidence: string;
      },
    ) =>
      req<{ consent: unknown }>(
        `/patients/${patientId}/communication-consents`,
        { method: "PUT", body: json(input) },
      ),
    list: () =>
      req<{ deliveries: CommunicationDelivery[] }>("/communications").then(
        (r) => r.deliveries,
      ),
    retry: (id: string) =>
      req<{ delivery: CommunicationDelivery }>(`/communications/${id}/retry`, {
        method: "POST",
      }).then((r) => r.delivery),
    configuration: () =>
      req<{
        templates: CommunicationTemplate[];
        rules: CommunicationRule[];
        whatsappConnections: Array<{
          locationId: string;
          locationName: string;
          status: "pending" | "connected" | "disconnected" | "error" | null;
          connectionType: "coexistence" | "dedicated" | null;
          displayPhoneMasked: string | null;
          appointmentReminderTemplateName: string | null;
          treatmentRenewalTemplateName: string | null;
          surveyTemplateName: string | null;
          marketingTemplateName: string | null;
          templateLanguage: string | null;
        }>;
      }>("/communications/configuration"),
    startWhatsAppSetup: (locationId: string) =>
      req<{ url: string }>("/communications/whatsapp/setup", {
        method: "POST",
        body: json({ locationId, returnOrigin: window.location.origin }),
      }),
    completeWhatsAppSetup: (input: {
      locationId: string;
      setupLinkId: string;
      phoneNumberId: string;
    }) =>
      req<{ connected: boolean }>("/communications/whatsapp/setup/complete", {
        method: "POST",
        body: json(input),
      }),
    preview: (input: {
      subject?: string | null;
      body: string;
      variables?: Record<string, string>;
    }) =>
      req<{
        subject: string | null;
        body: string;
        unknown: string[];
        missing: string[];
      }>("/communications/templates/preview", {
        method: "POST",
        body: json(input),
      }),
    createTemplate: (input: {
      name: string;
      eventType: CommunicationEventType;
      channel: CommunicationChannel;
      locale: string;
      subject?: string | null;
      body: string;
    }) =>
      req<{ template: CommunicationTemplate }>("/communications/templates", {
        method: "POST",
        body: json(input),
      }).then((r) => r.template),
    createRule: (input: {
      name: string;
      eventType: CommunicationEventType;
      channel: CommunicationChannel;
      templateId: string;
      delayMinutes: number;
    }) =>
      req<{ rule: CommunicationRule }>("/communications/rules", {
        method: "POST",
        body: json(input),
      }).then((r) => r.rule),
    updateTemplate: (id: string, enabled: boolean) =>
      req<{ template: CommunicationTemplate }>(
        `/communications/templates/${id}`,
        {
          method: "PATCH",
          body: json({ enabled }),
        },
      ).then((r) => r.template),
    updateRule: (
      id: string,
      input: Partial<
        Pick<
          CommunicationRule,
          "name" | "templateId" | "delayMinutes" | "enabled"
        >
      >,
    ) =>
      req<{ rule: CommunicationRule }>(`/communications/rules/${id}`, {
        method: "PATCH",
        body: json(input),
      }).then((r) => r.rule),
    previewWhatsAppCampaign: (input: {
      name: string;
      message: string;
      emailSubject: string | null;
      channels: Array<"email" | "whatsapp">;
      locationId: string;
      segment: {
        tags: string[];
        treatmentId: string | null;
        monthsSinceLastVisit: number | null;
        birthdayMonth: number | null;
      };
      promotionalPrice: number | null;
      validUntil: number | null;
      bookingUrl: string | null;
    }) =>
      req<{
        audienceSize: number;
        patientIds: string[];
        channelCounts: Partial<Record<"email" | "whatsapp", number>>;
        sample: Array<{
          id: string;
          name: string;
          recipients: Partial<Record<"email" | "whatsapp", string>>;
        }>;
      }>("/communications/whatsapp-campaigns/preview", {
        method: "POST",
        body: json(input),
      }),
    prepareWhatsAppCampaign: (input: {
      name: string;
      message: string;
      emailSubject: string | null;
      channels: Array<"email" | "whatsapp">;
      locationId: string;
      segment: {
        tags: string[];
        treatmentId: string | null;
        monthsSinceLastVisit: number | null;
        birthdayMonth: number | null;
      };
      promotionalPrice: number | null;
      validUntil: number | null;
      bookingUrl: string | null;
      confirmedAudienceSize: number;
    }) =>
      req<{ campaignId: string; prepared: number }>(
        "/communications/whatsapp-campaigns/prepare",
        { method: "POST", body: json(input) },
      ),
    configureWhatsAppTemplates: (
      locationId: string,
      input: {
        appointmentReminderTemplateName: string | null;
        treatmentRenewalTemplateName: string | null;
        surveyTemplateName: string | null;
        marketingTemplateName: string | null;
        templateLanguage: string;
      },
    ) =>
      req<{ configured: boolean }>(
        `/communications/whatsapp/locations/${locationId}/templates`,
        { method: "PUT", body: json(input) },
      ),
  },
  patientImports: {
    upload: (file: File) => {
      const format = file.name.toLowerCase().endsWith(".xlsx") ? "xlsx" : "csv";
      return req<{ job: PatientImportJob; headers: string[] }>(
        `/patient-imports?${new URLSearchParams({ filename: file.name, format })}`,
        {
          method: "POST",
          headers: {
            "content-type":
              format === "csv"
                ? "text/csv"
                : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
          body: file,
        },
      );
    },
    preview: (
      id: string,
      mapping: Partial<Record<PatientImportField, string>>,
      dateFormat?: "iso" | "dmy" | "mdy",
    ) =>
      req<{
        job: PatientImportJob;
        headers: string[];
        rows: PatientImportRow[];
      }>(`/patient-imports/${id}/preview`, {
        method: "POST",
        body: json({ mapping, dateFormat }),
      }),
    execute: (
      id: string,
      input: {
        atomic: boolean;
        decisions: Array<{
          rowNumber: number;
          decision: "create" | "skip" | "merge";
          targetPatientId?: string;
        }>;
      },
    ) =>
      req<{ job: PatientImportJob }>(`/patient-imports/${id}/execute`, {
        method: "POST",
        body: json(input),
      }).then((r) => r.job),
    errorReport: (id: string) => download(`/patient-imports/${id}/errors.csv`),
  },
  availability: {
    configuration: () =>
      req<{
        rules: AvailabilityRule[];
        exceptions: AvailabilityException[];
      }>("/availability/configuration"),
    createRule: (input: Omit<AvailabilityRule, "id">) =>
      req<{ rule: AvailabilityRule }>("/availability/rules", {
        method: "POST",
        body: json(input),
      }).then((result) => result.rule),
    createException: (input: Omit<AvailabilityException, "id">) =>
      req<{ exception: AvailabilityException }>("/availability/exceptions", {
        method: "POST",
        body: json(input),
      }).then((result) => result.exception),
    removeRule: (id: string) =>
      req<void>(`/availability/rules/${id}`, { method: "DELETE" }),
    removeException: (id: string) =>
      req<void>(`/availability/exceptions/${id}`, { method: "DELETE" }),
  },
  site: {
    get: () => req<ClinicSiteView>("/site"),
    uploadAsset: async (file: File) => {
      const session = await req<{
        token: string;
        uploadUrl: string;
        uploadMethod: "PUT";
        requiredHeaders: Record<string, string>;
        requiresApiAuthorization: boolean;
      }>("/site/assets", {
        method: "POST",
        body: json({ mimeType: file.type, byteSize: file.size }),
      });
      const headers = { ...session.requiredHeaders };
      if (session.requiresApiAuthorization) {
        const token = getToken();
        const organizationId = localStorage.getItem("dd-organization-id");
        if (token) headers.authorization = `Bearer ${token}`;
        if (organizationId) headers["x-organization-id"] = organizationId;
      }
      const uploaded = await fetch(session.uploadUrl, {
        method: session.uploadMethod,
        headers,
        body: file,
      });
      if (!uploaded.ok)
        throw new ApiError("No pudimos subir la imagen.", uploaded.status);
      return req<{ url: string }>(
        `/site/assets/${encodeURIComponent(session.token)}/complete`,
        { method: "POST" },
      );
    },
    checkSlug: (slug: string) =>
      req<SlugCheck>(`/site/slug-check${query({ slug })}`),
    saveSlug: (slug: string) =>
      req<ClinicSiteView>("/site/slug", {
        method: "PUT",
        body: json({ slug }),
      }),
    save: (
      input: Pick<
        ClinicSite,
        | "headline"
        | "subheadline"
        | "description"
        | "primaryCtaLabel"
        | "whatsappPhone"
        | "contactEmail"
        | "contactPhone"
        | "sections"
        | "faq"
        | "coverages"
        | "gallery"
        | "paletteId"
        | "cacheTtlSeconds"
      >,
    ) => req<ClinicSiteView>("/site", { method: "PUT", body: json(input) }),
    publish: () => req<ClinicSiteView>("/site/publish", { method: "POST" }),
    unpublish: () => req<ClinicSiteView>("/site/unpublish", { method: "POST" }),
    preview: () =>
      req<{ slug: string; token: string; expiresAt: number; url: string }>(
        "/site/preview",
        { method: "POST" },
      ),
    analytics: (since?: string) =>
      req<{ days: SiteVisitDay[] }>(`/site/analytics${query({ since })}`).then(
        (result) => result.days,
      ),
    profiles: {
      list: () =>
        req<{ profiles: ProfessionalProfile[] }>("/site/professionals").then(
          (result) => result.profiles,
        ),
      get: (professionalId: string) =>
        req<{ profile: ProfessionalProfile }>(
          `/site/professionals/${professionalId}`,
        ).then((result) => result.profile),
      checkSlug: (professionalId: string, slug: string) =>
        req<SlugCheck>(
          `/site/professionals/${professionalId}/slug-check${query({ slug })}`,
        ),
      save: (professionalId: string, input: ProfessionalProfileInput) =>
        req<{ profile: ProfessionalProfile }>(
          `/site/professionals/${professionalId}`,
          { method: "PUT", body: json(input) },
        ).then((result) => result.profile),
      saveSlug: (professionalId: string, slug: string) =>
        req<{ profile: ProfessionalProfile }>(
          `/site/professionals/${professionalId}/slug`,
          { method: "PUT", body: json({ slug }) },
        ).then((result) => result.profile),
      publish: (professionalId: string) =>
        req<{ profile: ProfessionalProfile }>(
          `/site/professionals/${professionalId}/publish`,
          { method: "POST" },
        ).then((result) => result.profile),
      unpublish: (professionalId: string) =>
        req<{ profile: ProfessionalProfile }>(
          `/site/professionals/${professionalId}/unpublish`,
          { method: "POST" },
        ).then((result) => result.profile),
    },
  },
  booking: {
    settings: () => req<BookingConfig>("/booking/settings"),
    saveSettings: (input: BookingSettings) =>
      req<BookingConfig>("/booking/settings", {
        method: "PUT",
        body: json(input),
      }),
    enable: () =>
      req<{ readiness: BookingReadiness }>("/onboarding/public-booking", {
        method: "POST",
      }).then((result) => result.readiness),
    disable: () =>
      req<void>("/onboarding/public-booking", { method: "DELETE" }),
    offerings: () =>
      req<{ offerings: BookingOffering[] }>("/booking/offerings").then(
        (result) => result.offerings,
      ),
    createOffering: (input: Omit<BookingOffering, "id" | "ownerId">) =>
      req<{ offering: BookingOffering }>("/booking/offerings", {
        method: "POST",
        body: json(input),
      }).then((result) => result.offering),
    updateOffering: (
      id: string,
      input: Partial<Omit<BookingOffering, "id" | "ownerId">>,
    ) =>
      req<{ offering: BookingOffering }>(`/booking/offerings/${id}`, {
        method: "PATCH",
        body: json(input),
      }).then((result) => result.offering),
    removeOffering: (id: string) =>
      req<void>(`/booking/offerings/${id}`, { method: "DELETE" }),
    requests: () =>
      req<{ requests: PublicBookingRequest[] }>("/booking/requests").then(
        (result) => result.requests,
      ),
  },
  ledger: {
    list: (patientId?: string) =>
      req<LedgerAccount>(`/ledger${query({ patientId })}`),
    charge: (input: {
      patientId: string;
      locationId: string | null;
      serviceLocationId?: string | null;
      appointmentId: string | null;
      budgetId: string | null;
      amount: number;
      currency: string;
      description: string;
      occurredAt: number;
      reference: string | null;
      idempotencyKey: string;
    }) =>
      req<{ transaction: LedgerTransaction }>("/ledger/charges", {
        method: "POST",
        body: json(input),
      }).then((result) => result.transaction),
    payment: (input: {
      patientId: string | null;
      locationId: string;
      serviceLocationId?: string | null;
      cashSessionId?: string | null;
      amount: number;
      currency: string;
      paymentMethod: NonNullable<LedgerTransaction["paymentMethod"]>;
      description: string;
      occurredAt: number;
      reference: string | null;
      idempotencyKey: string;
      applications: Array<{
        chargeTransactionId: string;
        amount: number;
        idempotencyKey: string;
      }>;
    }) =>
      req<{ transaction: LedgerTransaction }>("/ledger/payments", {
        method: "POST",
        body: json(input),
      }).then((result) => result.transaction),
    refund: (input: {
      patientId: string | null;
      locationId: string;
      paymentTransactionId: string;
      amount: number;
      currency: string;
      paymentMethod: NonNullable<LedgerTransaction["paymentMethod"]>;
      description: string;
      occurredAt: number;
      reference: string | null;
      idempotencyKey: string;
    }) =>
      req<{ transaction: LedgerTransaction }>("/ledger/refunds", {
        method: "POST",
        body: json(input),
      }).then((result) => result.transaction),
    reverse: (id: string, input: { reason: string; idempotencyKey: string }) =>
      req<{ transaction: LedgerTransaction }>(`/ledger/${id}/reverse`, {
        method: "POST",
        body: json(input),
      }).then((result) => result.transaction),
    receiptConfigurations: () =>
      req<{ configurations: ReceiptConfiguration[] }>(
        "/ledger/receipt-configurations",
      ).then((result) => result.configurations),
    configureReceipts: (input: {
      locationId: string | null;
      currency: string;
      prefix: string;
      nextNumber: number;
    }) =>
      req<{ configuration: ReceiptConfiguration }>(
        "/ledger/receipt-configurations",
        { method: "PUT", body: json(input) },
      ).then((result) => result.configuration),
  },
  treatmentBudgets: {
    list: (patientId: string) =>
      req<{ budgets: TreatmentBudget[] }>(
        `/treatment-budgets?patientId=${encodeURIComponent(patientId)}`,
      ).then((result) => result.budgets),
    create: (input: TreatmentBudgetDraft) =>
      req<{ budget: TreatmentBudget }>("/treatment-budgets", {
        method: "POST",
        body: json(input),
      }).then((result) => result.budget),
    revise: (id: string, input: TreatmentBudgetDraft) =>
      req<{ budget: TreatmentBudget }>(`/treatment-budgets/${id}/versions`, {
        method: "POST",
        body: json(input),
      }).then((result) => result.budget),
    send: (id: string) =>
      req<{ budget: TreatmentBudget; token: string }>(
        `/treatment-budgets/${id}/send`,
        { method: "POST" },
      ),
    comparison: (id: string) =>
      req<{
        budget: TreatmentBudget;
        comparison: {
          quoted: number;
          plannedUnits: number;
          convertedUnits: number;
          performedUnits: number;
          performedOutsideBudget: number;
          charged: number;
          collected: number;
        };
      }>(`/treatment-budgets/${id}`),
    convert: (
      id: string,
      itemId: string,
      input: {
        unitNumber: number;
        createAppointment: boolean;
        createCharge: boolean;
        startsAt?: number;
        durationMin?: number;
        professionalId?: string | null;
        locationId?: string | null;
      },
    ) =>
      req<{ fulfillment: TreatmentBudget["fulfillments"][number] }>(
        `/treatment-budgets/${id}/items/${itemId}/convert`,
        { method: "POST", body: json(input) },
      ).then((result) => result.fulfillment),
    pdf: (id: string) =>
      download(`/treatment-budgets/${encodeURIComponent(id)}/pdf`),
    preview: (token: string) =>
      req<
        TreatmentBudgetVersion & {
          clinic: string;
          patient: string;
          expired: boolean;
        }
      >(`/public/treatment-budgets/${encodeURIComponent(token)}`),
    accept: (
      token: string,
      input: {
        decision: "accepted" | "partially_accepted" | "rejected";
        signerName: string;
        signerDocument: string | null;
        items: Array<{ itemId: string; acceptedQuantity: number }>;
      },
    ) =>
      req<TreatmentBudgetVersion>(
        `/public/treatment-budgets/${encodeURIComponent(token)}/accept`,
        { method: "POST", body: json(input) },
      ),
    publicPdfUrl: (token: string) =>
      `${API_URL}/api/public/treatment-budgets/${encodeURIComponent(token)}/pdf`,
  },
  referrals: {
    contacts: (includeInactive?: boolean) =>
      req<{ contacts: ReferralContact[] }>(
        `/referral-contacts${query({ includeInactive: includeInactive ? "true" : undefined })}`,
      ).then((result) => result.contacts),
    createContact: (input: Omit<ReferralContact, "id" | "active">) =>
      req<{ contact: ReferralContact }>("/referral-contacts", {
        method: "POST",
        body: json(input),
      }).then((result) => result.contact),
    /* Dar de baja es esto mismo con `active: false`: las derivaciones ya
       hechas apuntan a su contacto, así que borrarlo las dejaría contando una
       historia incompleta. */
    updateContact: (id: string, input: Omit<ReferralContact, "id">) =>
      req<{ contact: ReferralContact }>(
        `/referral-contacts/${encodeURIComponent(id)}`,
        { method: "PATCH", body: json(input) },
      ).then((result) => result.contact),
    list: (patientId?: string, locationId?: string) =>
      req<{ referrals: Referral[] }>(
        `/referrals${query({ patientId, locationId })}`,
      ).then((result) => result.referrals),
    inbox: () =>
      req<{ referrals: Referral[] }>("/referrals/inbox").then(
        (result) => result.referrals,
      ),
    metrics: (locationId?: string) =>
      req<{ metrics: ReferralMetrics }>(
        `/referrals/metrics${query({ locationId })}`,
      ).then((result) => result.metrics),
    create: (input: {
      patientId: string;
      direction: "incoming" | "outgoing";
      originContactId: string | null;
      destinationContactId: string | null;
      specialty: string;
      reason: string;
      priority: "routine" | "priority" | "urgent";
      responsibleProfessionalId: string | null;
      sharedSections: Referral["sharedSections"];
      clinicalSummary: string | null;
      attachments: Array<{ uploadId: string; label: string }>;
    }) =>
      req<{ referral: Referral }>("/referrals", {
        method: "POST",
        body: json(input),
      }).then((result) => result.referral),
    send: (id: string) =>
      req<{ referral: Referral; token: string; expiresAt: number }>(
        `/referrals/${id}/send`,
        { method: "POST" },
      ),
    transition: (
      id: string,
      input: {
        status: ReferralStatus;
        note?: string | null;
        scheduledAt?: number | null;
        outcome?: string | null;
        closeReason?: string | null;
      },
    ) =>
      req<{ referral: Referral }>(`/referrals/${id}/status`, {
        method: "POST",
        body: json(input),
      }).then((result) => result.referral),
    preview: (token: string) =>
      req<{
        id: string;
        specialty: string;
        reason: string;
        priority: string;
        status: ReferralStatus;
        patient: string;
        summary: string | null;
        allergies: string | null;
        medications: string | null;
        problems: Array<{
          title: string;
          status: string;
          severity: string | null;
          onsetDate: string | null;
        }>;
        orders: Array<{
          title: string;
          type: string;
          clinicalQuestion: string | null;
          requestedAt: number;
        }>;
        attachments: Array<{ id: string; label: string }>;
        reports: Array<{
          version: number;
          body: string;
          signerName: string;
          createdAt: number;
        }>;
        expiresAt: number;
      }>(`/public/referrals/${encodeURIComponent(token)}`),
    counterReport: (
      token: string,
      input: { body: string; signerName: string },
    ) =>
      req<unknown>(
        `/public/referrals/${encodeURIComponent(token)}/counter-report`,
        { method: "POST", body: json(input) },
      ),
    attachmentUrl: (token: string, id: string) =>
      `${API_URL}/api/public/referrals/${encodeURIComponent(token)}/attachments/${encodeURIComponent(id)}`,
  },
  clinicalForms: {
    presets: () =>
      req<{
        presets: Array<{
          id: string;
          name: string;
          specialty: string;
          definition: ClinicalFormTemplate["definition"];
        }>;
      }>("/clinical-form-presets").then((result) => result.presets),
    list: () =>
      req<{
        templates: ClinicalFormTemplate[];
        assignments: ClinicalFormAssignment[];
      }>("/clinical-form-templates"),
    resolve: (input: { specialty?: string; appointmentType?: string }) =>
      req<{ template: ClinicalFormTemplate | null }>(
        `/clinical-form-template-resolution${query(input)}`,
      ).then((result) => result.template),
    /* Una plantilla propia, sin partir de un paquete inicial. El servidor lo
       soportaba desde siempre; lo que faltaba era pedirlo desde acá. */
    create: (input: {
      name: string;
      specialty: string | null;
      definition: ClinicalFormTemplate["definition"];
    }) =>
      req<{ template: ClinicalFormTemplate }>("/clinical-form-templates", {
        method: "POST",
        body: json(input),
      }).then((result) => result.template),
    clonePreset: (presetId: string) =>
      req<{ template: ClinicalFormTemplate }>(
        "/clinical-form-templates/clone-preset",
        { method: "POST", body: json({ presetId }) },
      ).then((result) => result.template),
    update: (
      id: string,
      input: Partial<
        Pick<
          ClinicalFormTemplate,
          "name" | "specialty" | "appointmentType" | "definition"
        >
      >,
    ) =>
      req<{ template: ClinicalFormTemplate }>(
        `/clinical-form-templates/${id}`,
        { method: "PATCH", body: json(input) },
      ).then((result) => result.template),
    publish: (id: string) =>
      req<{ template: ClinicalFormTemplate }>(
        `/clinical-form-templates/${id}/publish`,
        { method: "POST" },
      ).then((result) => result.template),
    version: (id: string) =>
      req<{ template: ClinicalFormTemplate }>(
        `/clinical-form-templates/${id}/versions`,
        { method: "POST" },
      ).then((result) => result.template),
    retire: (id: string) =>
      req<{ template: ClinicalFormTemplate }>(
        `/clinical-form-templates/${id}/retire`,
        { method: "POST" },
      ).then((result) => result.template),
    restore: (id: string) =>
      req<{ template: ClinicalFormTemplate }>(
        `/clinical-form-templates/${id}/restore`,
        { method: "POST" },
      ).then((result) => result.template),
    remove: (id: string) =>
      req<void>(`/clinical-form-templates/${id}`, { method: "DELETE" }),
    assign: (
      id: string,
      input: {
        scope: "organization" | "specialty" | "appointment_type";
        scopeValue: string | null;
      },
    ) =>
      req<{ assignment: ClinicalFormAssignment }>(
        `/clinical-form-templates/${id}/assignment`,
        { method: "PUT", body: json(input) },
      ).then((result) => result.assignment),
    responses: (patientId: string) =>
      req<{ responses: ClinicalFormResponse[] }>(
        `/patients/${patientId}/clinical-forms`,
      ).then((result) => result.responses),
    createResponse: (input: {
      patientId: string;
      templateVersionId: string;
      answers: ClinicalFormResponse["answers"];
      complete: boolean;
    }) =>
      req<{ response: ClinicalFormResponse }>("/clinical-form-responses", {
        method: "POST",
        body: json(input),
      }).then((result) => result.response),
    export: (id: string) => download(`/clinical-form-responses/${id}/export`),
  },
  inbox: {
    list: (
      filters: {
        status?: InboxStatus;
        search?: string;
        assignee?: string;
        tag?: string;
        locationId?: string;
      } = {},
    ) =>
      req<{ conversations: InboxConversation[] }>(
        `/inbox${query(filters)}`,
      ).then((result) => result.conversations),
    metrics: (locationId?: string) =>
      req<{ metrics: InboxMetrics }>(
        `/inbox/metrics${query({ locationId })}`,
      ).then((result) => result.metrics),
    update: (
      id: string,
      input: Partial<
        Pick<
          InboxConversation,
          | "status"
          | "priority"
          | "assigneeUserId"
          | "assigneeName"
          | "team"
          | "tags"
          | "slaDueAt"
          | "snoozedUntil"
        >
      >,
    ) =>
      req<{ conversation: InboxConversation }>(`/inbox/${id}`, {
        method: "PATCH",
        body: json(input),
      }).then((result) => result.conversation),
    reply: (
      id: string,
      input: { channel: "email" | "whatsapp"; body: string },
    ) =>
      req<{ conversation: InboxConversation }>(`/inbox/${id}/messages`, {
        method: "POST",
        body: json(input),
      }).then((result) => result.conversation),
    note: (id: string, body: string) =>
      req(`/inbox/${id}/internal-notes`, {
        method: "POST",
        body: json({ body }),
      }),
    reviewIdentity: (id: string, action: "accept" | "reject") =>
      req(`/inbox/identity-suggestions/${id}/review`, {
        method: "POST",
        body: json({ action }),
      }),
    convert: (contactId: string) =>
      req<{ patientId: string }>(`/inbox/contacts/${contactId}/convert`, {
        method: "POST",
        body: json({}),
      }),
  },
  prescriptions: {
    list: (patientId?: string) =>
      req<{ prescriptions: Prescription[] }>(
        `/prescriptions${query({ patientId })}`,
      ).then((result) => result.prescriptions),
    context: (patientId: string) =>
      req<{
        patient: Patient;
        encounters: ClinicalEncounter[];
        medications: PatientMedication[];
        credentials: Array<{
          credential: ProfessionalCredential;
          professional: Professional;
        }>;
        credentialCandidates: Array<{
          credential: ProfessionalCredential;
          professional: Professional;
        }>;
        professionals: Professional[];
        connections: PrescriptionConnection[];
      }>(`/prescriptions/context${query({ patientId })}`),
    saveConnection: (input: {
      kind: "prescriber" | "payer";
      market: string;
      provider: string;
      mode: "sandbox" | "live";
      scopes: string[];
    }) =>
      req<{ connection: PrescriptionConnection }>("/prescription-connections", {
        method: "POST",
        body: json(input),
      }).then((result) => result.connection),
    create: (input: {
      patientId: string;
      encounterId: string;
      professionalId: string;
      credentialId: string;
      documentType: "electronic_prescription" | "professional_indication";
      connectionId: string;
      idempotencyKey: string;
      items: Array<{
        medicationId: string;
        dose: string;
        frequency: string;
        quantity: string | null;
        duration: string | null;
        instructions: string | null;
      }>;
    }) =>
      req<{ prescription: Prescription }>("/prescriptions", {
        method: "POST",
        body: json(input),
      }).then((result) => result.prescription),
    issue: (id: string) =>
      req<{ prescription: Prescription }>(`/prescriptions/${id}/issue`, {
        method: "POST",
      }).then((result) => result.prescription),
    void: (id: string, reason: string) =>
      req<{ prescription: Prescription }>(`/prescriptions/${id}/void`, {
        method: "POST",
        body: json({ reason }),
      }).then((result) => result.prescription),
    patientLink: (id: string) =>
      req<{ token: string; expiresAt: number }>(
        `/prescriptions/${id}/patient-link`,
        { method: "POST" },
      ),
  },
  dashboard: {
    /** Sin sede, el consolidado de la organización. */
    summary: (locationId?: string) =>
      req<{ summary: DashboardSummary }>(
        `/dashboard/summary${query({ locationId })}`,
      ).then((r) => r.summary),
  },

  patients: {
    list: (search?: string, locationId?: string) =>
      req<{ patients: Patient[] }>(
        `/patients${query({ search, locationId })}`,
      ).then((r) => r.patients),
    get: (id: string) =>
      req<{ patient: Patient }>(`/patients/${id}`).then((r) => r.patient),
    create: (input: PatientInput) =>
      req<{ patient: Patient }>("/patients", {
        method: "POST",
        body: json(input),
      }).then((r) => r.patient),
    update: (id: string, input: Partial<PatientInput>) =>
      req<{ patient: Patient }>(`/patients/${id}`, {
        method: "PATCH",
        body: json(input),
      }).then((r) => r.patient),
    remove: (id: string) => req<void>(`/patients/${id}`, { method: "DELETE" }),
  },

  retention: {
    policy: () =>
      req<{ policy: RetentionPolicy }>("/retention/policy").then(
        (r) => r.policy,
      ),
    updatePolicy: (
      input: Pick<
        RetentionPolicy,
        | "region"
        | "clinicalRetentionDays"
        | "fiscalRetentionDays"
        | "backupRetentionDays"
      >,
    ) =>
      req<{ policy: RetentionPolicy }>("/retention/policy", {
        method: "PATCH",
        body: json(input),
      }).then((r) => r.policy),
    patients: () =>
      req<{ patients: Patient[] }>("/retention/patients").then(
        (r) => r.patients,
      ),
    reactivate: (patientId: string) =>
      req<{ patient: Patient }>(`/retention/patients/${patientId}/reactivate`, {
        method: "POST",
      }).then((r) => r.patient),
    report: (patientId: string) =>
      req<RetentionImpact>(`/retention/patients/${patientId}/report`),
    requestDeletion: (patientId: string, reason: string) =>
      req<{ request: DeletionRequest }>(
        `/retention/patients/${patientId}/deletion-requests`,
        { method: "POST", body: json({ reason }) },
      ).then((r) => r.request),
    legalHold: (
      requestId: string,
      legalHold: boolean,
      reason?: string | null,
    ) =>
      req<{ request: DeletionRequest }>(
        `/retention/deletion-requests/${requestId}/legal-hold`,
        { method: "PATCH", body: json({ legalHold, reason: reason ?? null }) },
      ).then((r) => r.request),
    approve: (requestId: string, patientName: string, confirmation: string) =>
      req<{ request: DeletionRequest }>(
        `/retention/deletion-requests/${requestId}/approve`,
        { method: "POST", body: json({ patientName, confirmation }) },
      ).then((r) => r.request),
    execute: (
      requestId: string,
      patientName: string,
      confirmation: string,
      idempotencyKey: string,
    ) =>
      req<{ job: PurgeJob }>(
        `/retention/deletion-requests/${requestId}/execute`,
        {
          method: "POST",
          body: json({ patientName, confirmation, idempotencyKey }),
        },
      ).then((r) => r.job),
  },

  appointments: {
    list: (params: ListAppointmentsParams = {}) =>
      req<{ appointments: AppointmentWithPatient[] }>(
        `/appointments${query({ ...params })}`,
      ).then((r) => r.appointments),
    get: (id: string) =>
      req<{ appointment: Appointment }>(
        `/appointments/${encodeURIComponent(id)}`,
      ).then((r) => r.appointment),
    create: (input: AppointmentInput) =>
      req<{ appointment: Appointment }>("/appointments", {
        method: "POST",
        body: json(input),
      }).then((r) => r.appointment),
    update: (id: string, input: AppointmentUpdate) =>
      req<{ appointment: Appointment }>(`/appointments/${id}`, {
        method: "PATCH",
        body: json(input),
      }).then((r) => r.appointment),
    remove: (id: string) =>
      req<void>(`/appointments/${id}`, { method: "DELETE" }),
    /** Una consulta que ya pasó, cargada desde el historial sin turno. */
    recordVisit: (input: RecordVisitInput) =>
      req<{ appointment: Appointment }>("/appointments/visits", {
        method: "POST",
        body: json(input),
      }).then((r) => r.appointment),
  },

  scheduleAutomation: {
    waitlist: (status = "active") =>
      req<{ entries: ScheduleWaitlistEntry[] }>(
        `/schedule-waitlist${query({ status })}`,
      ).then((response) => response.entries),
    coverage: (from?: number, to?: number) =>
      req<{
        coverage: {
          releasedSlots: number;
          offers: number;
          accepted: number;
          coverageRate: number;
          acceptanceRate: number;
          medianAcceptanceMinutes: number | null;
        };
      }>(`/schedule-waitlist/coverage${query({ from, to })}`).then(
        (response) => response.coverage,
      ),
    joinWaitlist: (input: ScheduleWaitlistEntryInput) =>
      req<{ entry: ScheduleWaitlistEntry["entry"] }>("/schedule-waitlist", {
        method: "POST",
        body: json(input),
      }).then((response) => response.entry),
    leaveWaitlist: (id: string) =>
      req<void>(`/schedule-waitlist/${id}`, { method: "DELETE" }),
    createSeries: (input: {
      patientId: string;
      professionalId?: string | null;
      locationId?: string | null;
      startsAt: number;
      durationMin: number;
      reason?: string | null;
      additionalTreatments?: Array<{
        treatmentId: string | null;
        name: string;
      }>;
      modality?: "onsite" | "remote";
      timezone: string;
      frequency: "weekly" | "monthly";
      interval: number;
      occurrenceCount: number;
    }) =>
      req<{ series: { id: string }; appointments: Appointment[] }>(
        "/appointment-series",
        { method: "POST", body: json(input) },
      ),
    editSeries: (
      seriesId: string,
      input: {
        scope: "occurrence" | "future";
        occurrenceId: string;
        reason?: string | null;
        additionalTreatments?: Array<{
          treatmentId: string | null;
          name: string;
        }>;
        durationMin?: number;
        status?: AppointmentStatus;
      },
    ) =>
      req<{ updated: Appointment[]; pastPreserved: boolean }>(
        `/appointment-series/${seriesId}`,
        { method: "PATCH", body: json(input) },
      ),
    previewOffer: (token: string) =>
      req<{
        offer: {
          id: string;
          status: "pending" | "accepted" | "expired" | "cancelled" | "lost";
          startsAt: number;
          expiresAt: number;
          clinic: string;
          available: boolean;
        };
      }>(`/public/schedule-waitlist-offers/${encodeURIComponent(token)}`).then(
        (response) => response.offer,
      ),
    acceptOffer: (token: string) =>
      req<{ appointment: { id: string; startsAt: number } }>(
        `/public/schedule-waitlist-offers/${encodeURIComponent(token)}/accept`,
        { method: "POST" },
      ).then((response) => response.appointment),
  },

  resources: {
    list: (params: { locationId?: string; type?: string } = {}) =>
      req<{ resources: ScheduleResource[] }>(`/resources${query(params)}`).then(
        (response) => response.resources,
      ),
  },

  /** Las sedes de la clínica. */
  locations: {
    list: () =>
      req<{ locations: Location[] }>("/locations").then((r) => r.locations),
    /** Una fila por sede con el mes en curso: lo que el dueño mira. */
    panel: () => req<SedePanel>("/locations/panel"),
    create: (input: {
      name: string;
      address?: string | null;
      phone?: string | null;
    }) =>
      req<{ location: Location }>("/locations", {
        method: "POST",
        body: json(input),
      }).then((r) => r.location),
    update: (
      id: string,
      input: { name?: string; address?: string | null; phone?: string | null },
    ) =>
      req<{ location: Location }>(`/locations/${id}`, {
        method: "PATCH",
        body: json(input),
      }).then((r) => r.location),
    remove: (id: string) => req<void>(`/locations/${id}`, { method: "DELETE" }),
    /** Quiénes atienden en esa sede. Se manda la lista completa. */
    setTeam: (id: string, professionalIds: string[]) =>
      req<{ professionalIds: string[] }>(`/locations/${id}/professionals`, {
        method: "PUT",
        body: json({ professionalIds }),
      }),
  },

  /** La caja de cada sede: apertura, movimientos y arqueo. */
  cash: {
    sessions: (
      params: {
        locationId?: string;
        status?: "open" | "closed";
        from?: number;
      } = {},
    ) =>
      req<{ sessions: CashSession[] }>(`/cash/sessions${query(params)}`).then(
        (r) => r.sessions,
      ),
    session: (id: string) => req<CashSessionDetail>(`/cash/sessions/${id}`),
    open: (input: {
      locationId: string;
      shift?: string | null;
      openingFloat: number;
    }) =>
      req<{ session: CashSession }>("/cash/sessions", {
        method: "POST",
        body: json(input),
      }).then((r) => r.session),
    /** Lo esperado lo calcula la API: acá sólo viaja lo que se contó. */
    close: (
      id: string,
      input: { countedTotal: number; notes?: string | null },
    ) =>
      req<CashSessionDetail>(`/cash/sessions/${id}/close`, {
        method: "POST",
        body: json(input),
      }),
    movement: (
      id: string,
      input: {
        kind: CashMovement["kind"];
        amount: number;
        description: string;
        reason?: string | null;
      },
    ) =>
      req<{ movement: CashMovement }>(`/cash/sessions/${id}/movements`, {
        method: "POST",
        body: json(input),
      }).then((r) => r.movement),
  },

  /** Honorarios: el esquema de cada uno y la liquidación del período. */
  payouts: {
    schemes: (professionalId?: string) =>
      req<{ schemes: PayoutScheme[] }>(
        `/payouts/schemes${query({ professionalId })}`,
      ).then((r) => r.schemes),
    createScheme: (input: {
      professionalId: string;
      locationId?: string | null;
      treatmentId?: string | null;
      kind: PayoutScheme["kind"];
      percentageBps?: number | null;
      fixedAmount?: number | null;
      effectiveFrom: number;
    }) =>
      req<{ scheme: PayoutScheme }>("/payouts/schemes", {
        method: "POST",
        body: json(input),
      }).then((r) => r.scheme),
    /** Cierra la vigencia; no borra, para que las liquidaciones viejas cierren. */
    expireScheme: (id: string) =>
      req<{ scheme: PayoutScheme }>(`/payouts/schemes/${id}`, {
        method: "DELETE",
      }).then((r) => r.scheme),
    list: (params: { locationId?: string; professionalId?: string } = {}) =>
      req<{ payouts: ProfessionalPayout[] }>(`/payouts${query(params)}`).then(
        (r) => r.payouts,
      ),
    detail: (id: string) => req<PayoutDetail>(`/payouts/${id}`),
    draft: (input: {
      professionalId: string;
      locationId: string;
      periodStart: number;
      periodEnd: number;
      notes?: string | null;
    }) => req<PayoutDetail>("/payouts", { method: "POST", body: json(input) }),
    deduct: (
      id: string,
      input: {
        kind: PayoutDeduction["kind"];
        description: string;
        amount: number;
      },
    ) =>
      req<PayoutDetail>(`/payouts/${id}/deductions`, {
        method: "POST",
        body: json(input),
      }),
    close: (id: string) =>
      req<PayoutDetail>(`/payouts/${id}/close`, { method: "POST" }),
    markPaid: (id: string) =>
      req<PayoutDetail>(`/payouts/${id}/paid`, { method: "POST" }),
  },

  /** El catálogo de lo que la clínica ofrece. */
  treatments: {
    list: () =>
      req<{ treatments: Treatment[] }>("/treatments").then((r) => r.treatments),
    create: (input: {
      name: string;
      category?: string;
      renewalIntervalMonths?: number | null;
    }) =>
      req<{ treatment: Treatment }>("/treatments", {
        method: "POST",
        body: json(input),
      }).then((r) => r.treatment),
    update: (
      id: string,
      input: {
        name?: string;
        category?: string;
        renewalIntervalMonths?: number | null;
      },
    ) =>
      req<{ treatment: Treatment }>(`/treatments/${id}`, {
        method: "PATCH",
        body: json(input),
      }).then((r) => r.treatment),
    setDefaultSupplies: (
      id: string,
      supplies: Array<{ itemId: string; quantity: number }>,
    ) =>
      req<{ supplies: Array<{ itemId: string; quantity: number }> }>(
        `/treatments/${id}/default-supplies`,
        { method: "PUT", body: json({ supplies }) },
      ).then((response) => response.supplies),
    remove: (id: string) =>
      req<void>(`/treatments/${id}`, { method: "DELETE" }),
  },

  /** Campañas publicitarias y lo que volvió de cada una. */
  campaigns: {
    list: () =>
      req<{ campaigns: CampaignReturn[] }>("/campaigns").then(
        (r) => r.campaigns,
      ),
    create: (input: CampaignInput) =>
      req<{ campaign: CampaignReturn }>("/campaigns", {
        method: "POST",
        body: json(input),
      }).then((r) => r.campaign),
    update: (id: string, input: Partial<CampaignInput>) =>
      req<{ campaign: CampaignReturn }>(`/campaigns/${id}`, {
        method: "PATCH",
        body: json(input),
      }).then((r) => r.campaign),
    remove: (id: string) => req<void>(`/campaigns/${id}`, { method: "DELETE" }),
  },

  /** El equipo que atiende, administrado desde Organización. */
  professionals: {
    list: (
      filters: {
        q?: string;
        status?: Professional["status"];
        specialtyId?: string;
        locationId?: string;
        modality?: "onsite" | "remote";
      } = {},
    ) =>
      req<{ professionals: Professional[] }>(
        `/professionals${query(filters)}`,
      ).then((r) => r.professionals),
    create: (input: {
      name: string;
      role: string;
      email?: string | null;
      modality?: Professional["modality"];
      defaultDurationMin?: number;
    }) =>
      req<{ professional: Professional }>("/professionals", {
        method: "POST",
        body: json(input),
      }).then((r) => r.professional),
    update: (
      id: string,
      input: {
        name?: string;
        role?: string;
        email?: string | null;
        status?: Professional["status"];
        modality?: Professional["modality"];
        defaultDurationMin?: number;
        /** Las sedes donde atiende, completas. Vacío es "en todas". */
        locationIds?: string[];
      },
    ) =>
      req<{ professional: Professional }>(`/professionals/${id}`, {
        method: "PATCH",
        body: json(input),
      }).then((r) => r.professional),
    remove: (id: string) =>
      req<void>(`/professionals/${id}`, { method: "DELETE" }),
    detail: (id: string) =>
      req<{ professional: ProfessionalDetail }>(
        `/professionals/${id}/detail`,
      ).then((r) => r.professional),
    activity: (id: string, locationId?: string) =>
      req<{ activity: ProfessionalActivity[] }>(
        `/professionals/${id}/activity${query({ locationId })}`,
      ).then((r) => r.activity),
    administrativeHistory: (id: string) =>
      req<{ events: AuditEvent[] }>(
        `/professionals/${id}/administrative-history`,
      ).then((r) => r.events),
    credentials: (id: string) =>
      req<{ credentials: ProfessionalCredential[] }>(
        `/professionals/${id}/credentials`,
      ).then((r) => r.credentials),
    createCredential: (
      professionalId: string,
      input: {
        kind: string;
        number: string;
        jurisdiction: string;
        issuedAt?: number | null;
        expiresAt?: number | null;
        note?: string | null;
      },
    ) =>
      req<{ credential: ProfessionalCredential }>(
        `/professionals/${professionalId}/credentials`,
        { method: "POST", body: json(input) },
      ).then((r) => r.credential),
    updateCredential: (
      credentialId: string,
      input: {
        kind?: string;
        number?: string;
        jurisdiction?: string;
        issuedAt?: number | null;
        expiresAt?: number | null;
        note?: string | null;
      },
    ) =>
      req<{ credential: ProfessionalCredential }>(
        `/credentials/${credentialId}`,
        { method: "PATCH", body: json(input) },
      ).then((r) => r.credential),
    verifyCredential: (
      credentialId: string,
      input: {
        status: "unverified" | "pending" | "verified" | "rejected";
        note?: string;
      },
    ) =>
      req<{ credential: ProfessionalCredential }>(
        `/credentials/${credentialId}/verification`,
        { method: "POST", body: json(input) },
      ).then((r) => r.credential),
    metrics: (
      id: string,
      filters: {
        from: number;
        to: number;
        locationId?: string;
        specialtyId?: string;
        modality?: "onsite" | "remote";
        appointmentTypeId?: string;
      },
    ) =>
      req<ProfessionalMetricsReport>(
        `/professionals/${id}/metrics${query(filters)}`,
      ),
    capacity: (filters: {
      from: number;
      to: number;
      locationId?: string;
      specialtyId?: string;
      modality?: "onsite" | "remote";
      appointmentTypeId?: string;
    }) =>
      req<ProfessionalCapacityReport>(
        `/professional-capacity${query(filters)}`,
      ),
    exportCapacity: (filters: {
      from: number;
      to: number;
      locationId?: string;
      specialtyId?: string;
      modality?: "onsite" | "remote";
      appointmentTypeId?: string;
    }) => download(`/professional-capacity/export${query(filters)}`),
  },

  specialties: {
    list: () =>
      req<{ specialties: Specialty[] }>("/specialties").then(
        (result) => result.specialties,
      ),
  },

  preconsultations: {
    templates: () =>
      req<{
        templates: Array<{
          template: PreconsultationTemplate;
          version: PreconsultationTemplateVersion;
        }>;
      }>("/preconsultation-templates").then((result) => result.templates),
    createTemplate: (input: {
      name: string;
      description?: string | null;
      fields: PreconsultationField[];
    }) =>
      req<{
        template: PreconsultationTemplate;
        version: PreconsultationTemplateVersion;
      }>("/preconsultation-templates", { method: "POST", body: json(input) }),
    versionTemplate: (id: string, fields: PreconsultationField[]) =>
      req<{ version: PreconsultationTemplateVersion }>(
        `/preconsultation-templates/${id}/versions`,
        { method: "POST", body: json({ fields }) },
      ).then((result) => result.version),
    updateTemplate: (
      id: string,
      input: Partial<
        Pick<PreconsultationTemplate, "name" | "description" | "status">
      >,
    ) =>
      req<{ template: PreconsultationTemplate }>(
        `/preconsultation-templates/${id}`,
        { method: "PATCH", body: json(input) },
      ).then((result) => result.template),
    assign: (
      id: string,
      input: {
        appointmentTypeId: string | null;
        specialtyId: string | null;
        priority?: number;
      },
    ) =>
      req(`/preconsultation-templates/${id}/assignments`, {
        method: "POST",
        body: json(input),
      }),
    forAppointment: (appointmentId: string) =>
      req<{ submissions: PreconsultationBundle[] }>(
        `/appointments/${appointmentId}/preconsultations`,
      ).then((result) => result.submissions),
    review: (
      submissionId: string,
      fieldKey: string,
      input: {
        decision: "accepted" | "rejected" | "corrected";
        finalValue?: PreconsultationAnswer;
      },
    ) =>
      req(`/preconsultations/${submissionId}/reviews/${fieldKey}`, {
        method: "PUT",
        body: json(input),
      }),
    file: (submissionId: string, fieldKey: string) =>
      download(`/preconsultations/${submissionId}/files/${fieldKey}`),
    portalList: () =>
      req<{ submissions: PreconsultationBundle[] }>(
        "/portal/preconsultations",
      ).then((result) => result.submissions),
    portalOpen: (id: string) =>
      req<PreconsultationBundle>(`/portal/preconsultations/${id}`),
    saveDraft: (
      id: string,
      draftVersion: number,
      answers: Record<string, PreconsultationAnswer>,
    ) =>
      req<{ submission: PreconsultationSubmission }>(
        `/portal/preconsultations/${id}/draft`,
        { method: "PUT", body: json({ draftVersion, answers }) },
      ).then((result) => result.submission),
    complete: (id: string) =>
      req<{ submission: PreconsultationSubmission }>(
        `/portal/preconsultations/${id}/complete`,
        { method: "POST" },
      ).then((result) => result.submission),
    uploadFile: async (submissionId: string, file: File) => {
      const session = await req<ClinicalUploadSession>(
        `/portal/preconsultations/${submissionId}/uploads`,
        {
          method: "POST",
          body: json({ mimeType: file.type, byteSize: file.size }),
        },
      );
      const headers = { ...session.requiredHeaders };
      if (session.requiresApiAuthorization) {
        const token = getToken();
        if (token) headers.authorization = `Bearer ${token}`;
      }
      const response = await fetch(session.uploadUrl, {
        method: session.uploadMethod,
        headers,
        body: file,
      });
      if (!response.ok)
        throw new ApiError("No pudimos subir el archivo.", response.status);
      await req(
        `/portal/preconsultations/${submissionId}/uploads/${session.upload.id}/complete`,
        { method: "POST" },
      );
      return session.upload.id;
    },
  },

  calendars: {
    list: (professionalId?: string) =>
      req<{ connections: CalendarConnection[] }>(
        `/calendar-connections${query({ professionalId })}`,
      ).then((response) => response.connections),
    authorize: (professionalId: string, provider: "google" | "microsoft") =>
      req<{ authorizationUrl: string }>(
        `/calendar-connections/${professionalId}/${provider}/authorize`,
        { method: "POST" },
      ),
    available: (connectionId: string) =>
      req<{ calendars: ProviderCalendar[] }>(
        `/calendar-connections/${connectionId}/calendars`,
      ).then((response) => response.calendars),
    select: (
      connectionId: string,
      calendars: Array<{
        id: string;
        blocksAvailability: boolean;
        publishesAppointments: boolean;
      }>,
    ) =>
      req<{ connections: CalendarConnection[] }>(
        `/calendar-connections/${connectionId}/calendars`,
        { method: "PUT", body: json({ calendars }) },
      ).then((response) => response.connections),
    sync: (connectionId: string) =>
      req<{ syncedAt: number }>(`/calendar-connections/${connectionId}/sync`, {
        method: "POST",
      }),
    revoke: (connectionId: string) =>
      req<void>(`/calendar-connections/${connectionId}`, { method: "DELETE" }),
  },

  /**
   * Fotos clínicas. El archivo no se pone en un `<img src>` directo: la ruta
   * exige sesión, así que se descarga con el token y se muestra desde memoria.
   */
  photos: {
    listByPatient: (patientId: string) =>
      req<{ photos: PatientPhoto[] }>(`/patients/${patientId}/photos`).then(
        (r) => r.photos,
      ),
    create: (
      patientId: string,
      input: {
        kind: PhotoKind;
        mimeType: string;
        data?: string;
        uploadId?: string;
        appointmentId?: string | null;
        note?: string | null;
      },
    ) =>
      req<{ photo: PatientPhoto }>(`/patients/${patientId}/photos`, {
        method: "POST",
        body: json(input),
      }).then((r) => r.photo),
    remove: (photoId: string) =>
      req<void>(`/photos/${photoId}`, { method: "DELETE" }),
    /** El archivo en sí, ya con la sesión puesta. */
    file: (photoId: string) =>
      clinicalFileBlob(`/photos/${photoId}`, "No pudimos cargar la foto."),
  },

  /** Consentimientos firmados y su documento. */
  consents: {
    listByPatient: (patientId: string) =>
      req<{ consents: ConsentDocument[] }>(
        `/patients/${patientId}/consents`,
      ).then((r) => r.consents),
    create: (
      patientId: string,
      input: {
        kind: ConsentKind;
        title: string;
        mimeType: string;
        data?: string;
        uploadId?: string;
        signedAt: number;
        signerContactId?: string | null;
        signerCapacity?: string | null;
        appointmentId?: string | null;
        note?: string | null;
      },
    ) =>
      req<{ consent: ConsentDocument }>(`/patients/${patientId}/consents`, {
        method: "POST",
        body: json(input),
      }).then((r) => r.consent),
    /** El archivo, con la sesión puesta: no hay carpeta pública. */
    file: (consentId: string) =>
      clinicalFileBlob(
        `/consents/${consentId}`,
        "No pudimos abrir el documento.",
      ),
    remove: (consentId: string) =>
      req<void>(`/consents/${consentId}`, { method: "DELETE" }),
  },

  /** Historial clínico: lo escrito a partir de cada consulta. */
  notes: {
    listByPatient: (patientId: string) =>
      req<{ notes: ConsultationNoteWithAppointment[] }>(
        `/patients/${patientId}/notes`,
      ).then((r) => r.notes),
    create: (patientId: string, input: NoteInput) =>
      req<{ note: ConsultationNote }>(`/patients/${patientId}/notes`, {
        method: "POST",
        body: json(input),
      }).then((r) => r.note),
    /** El adjunto de la nota, ya con la sesión puesta. */
    file: (noteId: string) =>
      fileBlob(`/notes/${noteId}/file`, "No pudimos abrir el archivo."),
    remove: (noteId: string) =>
      req<void>(`/notes/${noteId}`, { method: "DELETE" }),
  },

  /**
   * Alergias y medicación: lo que hay que leer antes de escribir una receta.
   *
   * Nada de esto se borra. Un registro equivocado se marca como error y una
   * medicación que se dejó de tomar se suspende; en los dos casos queda,
   * porque lo que fue cierto en marzo tiene que seguir diciéndolo en octubre.
   */
  clinicalProfile: {
    get: (patientId: string) =>
      req<ClinicalProfileSummary>(`/patients/${patientId}/clinical-profile`),
    history: (patientId: string, subjectId?: string) =>
      req<{ events: ClinicalProfileEvent[] }>(
        `/patients/${patientId}/clinical-profile/history${
          subjectId ? `?subjectId=${encodeURIComponent(subjectId)}` : ""
        }`,
      ).then((r) => r.events),
    listAllergies: (patientId: string, all = false) =>
      req<{ allergies: PatientAllergy[] }>(
        `/patients/${patientId}/allergies?status=${all ? "all" : "active"}`,
      ).then((r) => r.allergies),
    addAllergy: (
      patientId: string,
      input: {
        substance: string;
        category: AllergyCategory;
        severity: AllergySeverity;
        source?: ClinicalSource;
        reaction?: string | null;
        onsetDate?: string | null;
        note?: string | null;
        encounterId?: string | null;
        acknowledgeDuplicate?: boolean;
      },
    ) =>
      req<{ allergy: PatientAllergy; duplicateOf: string[] }>(
        `/patients/${patientId}/allergies`,
        { method: "POST", body: json(input) },
      ),
    updateAllergy: (
      patientId: string,
      id: string,
      input: Partial<{
        substance: string;
        category: AllergyCategory;
        severity: AllergySeverity;
        source: ClinicalSource;
        reaction: string | null;
        onsetDate: string | null;
        note: string | null;
        encounterId: string | null;
      }>,
    ) =>
      req<{ allergy: PatientAllergy }>(
        `/patients/${patientId}/allergies/${id}`,
        { method: "PATCH", body: json(input) },
      ).then((r) => r.allergy),
    setAllergyStatus: (
      patientId: string,
      id: string,
      input: {
        status: AllergyStatus;
        reason?: string | null;
        encounterId?: string | null;
      },
    ) =>
      req<{ allergy: PatientAllergy }>(
        `/patients/${patientId}/allergies/${id}/status`,
        { method: "POST", body: json(input) },
      ).then((r) => r.allergy),
    /** Afirmar "sin alergias conocidas", o volver a "sin datos". */
    setNoKnownAllergies: (patientId: string, asserted: boolean) =>
      req<ClinicalProfileSummary>(`/patients/${patientId}/no-known-allergies`, {
        method: "PUT",
        body: json({ asserted }),
      }),
    listMedications: (patientId: string, all = false) =>
      req<{ medications: PatientMedication[] }>(
        `/patients/${patientId}/medications?status=${all ? "all" : "active"}`,
      ).then((r) => r.medications),
    addMedication: (
      patientId: string,
      input: {
        name: string;
        form?: string | null;
        dose?: string | null;
        route?: string | null;
        frequency?: string | null;
        indication?: string | null;
        source?: ClinicalSource;
        startedOn?: string | null;
        note?: string | null;
        encounterId?: string | null;
        acknowledgeDuplicate?: boolean;
      },
    ) =>
      req<{ medication: PatientMedication; duplicateOf: string[] }>(
        `/patients/${patientId}/medications`,
        { method: "POST", body: json(input) },
      ),
    updateMedication: (
      patientId: string,
      id: string,
      input: Partial<{
        name: string;
        form: string | null;
        dose: string | null;
        route: string | null;
        frequency: string | null;
        indication: string | null;
        source: ClinicalSource;
        startedOn: string | null;
        note: string | null;
        encounterId: string | null;
      }>,
    ) =>
      req<{ medication: PatientMedication }>(
        `/patients/${patientId}/medications/${id}`,
        { method: "PATCH", body: json(input) },
      ).then((r) => r.medication),
    setMedicationStatus: (
      patientId: string,
      id: string,
      input: {
        status: MedicationStatus;
        reason?: string | null;
        endedOn?: string | null;
        encounterId?: string | null;
      },
    ) =>
      req<{ medication: PatientMedication }>(
        `/patients/${patientId}/medications/${id}/status`,
        { method: "POST", body: json(input) },
      ).then((r) => r.medication),
  },
  /**
   * La lista de problemas y los diagnósticos de cada encuentro.
   *
   * Son dos cosas distintas a propósito: el problema es longitudinal y cambia
   * de estado, el diagnóstico queda congelado con la firma del encuentro que
   * lo afirmó. Por eso corregir uno firmado va por adenda y no por PATCH.
   */
  problems: {
    summary: (patientId: string) =>
      req<ProblemSummary>(`/patients/${patientId}/problems/summary`),
    list: (
      patientId: string,
      status: "active" | "resolved" | "all" = "active",
    ) =>
      req<{ problems: PatientProblem[] }>(
        `/patients/${patientId}/problems?status=${status}`,
      ).then((r) => r.problems),
    history: (patientId: string, problemId?: string) =>
      req<{ events: ProblemEvent[] }>(
        `/patients/${patientId}/problems/history${
          problemId ? `?problemId=${encodeURIComponent(problemId)}` : ""
        }`,
      ).then((r) => r.events),
    create: (
      patientId: string,
      input: {
        title: string;
        code?: string | null;
        codeSystem?: ProblemCodeSystem | null;
        severity?: ProblemSeverity | null;
        onsetDate?: string | null;
        note?: string | null;
        encounterId?: string | null;
        acknowledgeDuplicate?: boolean;
      },
    ) =>
      req<{ problem: PatientProblem; duplicateOf: string[] }>(
        `/patients/${patientId}/problems`,
        { method: "POST", body: json(input) },
      ),
    update: (
      patientId: string,
      id: string,
      input: Partial<{
        title: string;
        code: string | null;
        codeSystem: ProblemCodeSystem | null;
        severity: ProblemSeverity | null;
        onsetDate: string | null;
        note: string | null;
        encounterId: string | null;
      }>,
    ) =>
      req<{ problem: PatientProblem }>(
        `/patients/${patientId}/problems/${id}`,
        {
          method: "PATCH",
          body: json(input),
        },
      ).then((r) => r.problem),
    setStatus: (
      patientId: string,
      id: string,
      input: {
        status: ProblemStatus;
        reason?: string | null;
        resolvedDate?: string | null;
        encounterId?: string | null;
      },
    ) =>
      req<{ problem: PatientProblem }>(
        `/patients/${patientId}/problems/${id}/status`,
        { method: "POST", body: json(input) },
      ).then((r) => r.problem),
    /** `null` devuelve la ficha a "sin datos". */
    setListState: (
      patientId: string,
      state: "none_reported" | "not_assessed" | null,
    ) =>
      req<ProblemSummary>(`/patients/${patientId}/problem-list-state`, {
        method: "PUT",
        body: json({ state }),
      }),
    listDiagnoses: (encounterId: string) =>
      req<{ diagnoses: EncounterDiagnosis[] }>(
        `/encounters/${encounterId}/diagnoses`,
      ).then((r) => r.diagnoses),
    addDiagnosis: (
      encounterId: string,
      input: {
        text: string;
        code?: string | null;
        codeSystem?: ProblemCodeSystem | null;
        primary?: boolean;
        problemId?: string | null;
        addToProblemList?: boolean;
      },
    ) =>
      req<{ diagnosis: EncounterDiagnosis }>(
        `/encounters/${encounterId}/diagnoses`,
        { method: "POST", body: json(input) },
      ).then((r) => r.diagnosis),
  },
  /**
   * Mediciones clínicas comparables en el tiempo.
   *
   * Nada se pisa: corregir una medición inserta la corrección y marca la
   * anterior como reemplazada, porque con el valor viejo pudo haberse tomado
   * una decisión y la historia tiene que poder mostrarlo.
   */
  vitals: {
    summary: (patientId: string) =>
      req<VitalsSummary>(`/patients/${patientId}/vitals/summary`),
    list: (
      patientId: string,
      params: { type?: VitalType; includeSuperseded?: boolean } = {},
    ) => {
      const query = new URLSearchParams();
      if (params.type) query.set("type", params.type);
      if (params.includeSuperseded) query.set("includeSuperseded", "true");
      const suffix = query.toString() ? `?${query}` : "";
      return req<{ vitals: PatientVital[] }>(
        `/patients/${patientId}/vitals${suffix}`,
      ).then((r) => r.vitals);
    },
    series: (patientId: string, type: VitalType) =>
      req<VitalSeries>(`/patients/${patientId}/vitals/series/${type}`),
    record: (
      patientId: string,
      input: {
        type: VitalType;
        value: number;
        secondaryValue?: number | null;
        unit: string;
        method?: string | null;
        measuredAt?: number;
        measuredByName?: string | null;
        note?: string | null;
        encounterId?: string | null;
        confirmOutOfRange?: boolean;
      },
    ) =>
      req<{ measurement: PatientVital }>(`/patients/${patientId}/vitals`, {
        method: "POST",
        body: json(input),
      }).then((r) => r.measurement),
    correct: (
      patientId: string,
      id: string,
      input: {
        type: VitalType;
        value: number;
        secondaryValue?: number | null;
        unit: string;
        reason: string;
        measuredAt?: number;
        measuredByName?: string | null;
        confirmOutOfRange?: boolean;
      },
    ) =>
      req<{ measurement: PatientVital; superseded: PatientVital }>(
        `/patients/${patientId}/vitals/${id}/correction`,
        { method: "POST", body: json(input) },
      ),
  },
  /**
   * Órdenes, resultados e indicaciones: el circuito de después de la consulta.
   *
   * Lo que importa acá es el estado `received`: llegó y todavía no lo miró
   * nadie. Esa es la tarea que la bandeja muestra hasta que alguien actúa.
   */
  orders: {
    inbox: () =>
      req<{ orders: InboxOrder[] }>("/clinical-orders/inbox").then(
        (r) => r.orders,
      ),
    listByPatient: (patientId: string, all = false) =>
      req<{ orders: ClinicalOrderWithResults[] }>(
        `/patients/${patientId}/orders?status=${all ? "all" : "open"}`,
      ).then((r) => r.orders),
    create: (
      patientId: string,
      input: {
        encounterId: string;
        type: OrderType;
        title: string;
        clinicalQuestion?: string | null;
        internalNote?: string | null;
      },
    ) =>
      req<{ order: ClinicalOrder }>(`/patients/${patientId}/orders`, {
        method: "POST",
        body: json(input),
      }).then((r) => r.order),
    addResult: (
      orderId: string,
      input: {
        summary: string;
        values?: { name: string; value: string; unit?: string }[];
        uploadId?: string | null;
        source?: ResultSource;
        sourceName?: string | null;
        performedAt?: number | null;
      },
    ) =>
      req<{ result: ClinicalOrderResult; order: ClinicalOrder }>(
        `/clinical-orders/${orderId}/results`,
        { method: "POST", body: json(input) },
      ),
    markReviewed: (orderId: string) =>
      req<{ order: ClinicalOrder }>(`/clinical-orders/${orderId}/reviewed`, {
        method: "POST",
        body: json({}),
      }).then((r) => r.order),
    markPatientNotified: (orderId: string, via: string) =>
      req<{ order: ClinicalOrder }>(
        `/clinical-orders/${orderId}/patient-notified`,
        { method: "POST", body: json({ via }) },
      ).then((r) => r.order),
    cancel: (orderId: string, reason: string) =>
      req<{ order: ClinicalOrder }>(`/clinical-orders/${orderId}/cancel`, {
        method: "POST",
        body: json({ reason }),
      }).then((r) => r.order),
    listInstructions: (patientId: string) =>
      req<{ instructions: ClinicalInstruction[] }>(
        `/patients/${patientId}/instructions`,
      ).then((r) => r.instructions),
    addInstruction: (
      patientId: string,
      input: {
        text: string;
        encounterId?: string | null;
        patientVisible?: boolean;
      },
    ) =>
      req<{ instruction: ClinicalInstruction }>(
        `/patients/${patientId}/instructions`,
        { method: "POST", body: json(input) },
      ).then((r) => r.instruction),
  },
  assistant: {
    query: (question: string, locationId?: string) =>
      req<{ response: PlatformAssistantResponse }>("/assistant/query", {
        method: "POST",
        body: json({ question, locationId }),
      }).then((result) => result.response),
  },
  encounters: {
    listByPatient: (patientId: string) =>
      req<{ encounters: ClinicalEncounter[] }>(
        `/patients/${patientId}/encounters`,
      ).then((r) => r.encounters),
    get: (id: string) =>
      req<{ encounter: ClinicalEncounter }>(`/encounters/${id}`).then(
        (r) => r.encounter,
      ),
    open: (input: {
      patientId: string;
      appointmentId?: string | null;
      professionalId?: string | null;
      locationId?: string | null;
      reason?: string;
      idempotencyKey: string;
    }) =>
      req<{ encounter: ClinicalEncounter }>("/encounters", {
        method: "POST",
        body: json(input),
      }).then((r) => r.encounter),
    save: (
      id: string,
      input: Pick<
        ClinicalEncounter,
        | "version"
        | "reason"
        | "anamnesis"
        | "examination"
        | "assessment"
        | "plan"
      > & { status?: "draft" | "in_progress" },
    ) =>
      req<{ encounter: ClinicalEncounter }>(`/encounters/${id}`, {
        method: "PATCH",
        body: json(input),
      }).then((r) => r.encounter),
    assistedDraft: (
      id: string,
      input: {
        transcript: string;
        consentConfirmed: true;
        locale?: string;
      },
    ) =>
      req<{ proposal: AssistedEncounterProposal }>(
        `/encounters/${id}/assisted-draft`,
        { method: "POST", body: json(input) },
      ).then((r) => r.proposal),
    sign: (id: string, version: number) =>
      req<{ encounter: ClinicalEncounter }>(`/encounters/${id}/sign`, {
        method: "POST",
        body: json({ version }),
      }).then((r) => r.encounter),
    addendum: (
      id: string,
      input: { version: number; reason: string; content: string },
    ) =>
      req<{ encounter: ClinicalEncounter }>(`/encounters/${id}/addenda`, {
        method: "POST",
        body: json(input),
      }),
    enterInError: (id: string, version: number, reason: string) =>
      req<{ encounter: ClinicalEncounter }>(
        `/encounters/${id}/entered-in-error`,
        { method: "POST", body: json({ version, reason }) },
      ).then((r) => r.encounter),
  },

  waitingRoom: {
    board: (
      params: {
        day?: string;
        locationId?: string;
        professionalId?: string;
      } = {},
    ) => {
      const query = new URLSearchParams(
        Object.entries(params).filter(([, value]) => Boolean(value)) as [
          string,
          string,
        ][],
      );
      return req<QueueBoard>(`/waiting-room${query.size ? `?${query}` : ""}`);
    },
    display: (params: { day?: string; locationId?: string } = {}) => {
      const query = new URLSearchParams(
        Object.entries(params).filter(([, value]) => Boolean(value)) as [
          string,
          string,
        ][],
      );
      return req<QueueDisplay>(
        `/waiting-room/display${query.size ? `?${query}` : ""}`,
      );
    },
    metrics: (params: {
      from: string;
      to: string;
      locationId?: string;
      professionalId?: string;
    }) =>
      req<QueueMetricsReport>(
        `/waiting-room/metrics?${new URLSearchParams(
          Object.entries(params).filter(([, value]) => Boolean(value)) as [
            string,
            string,
          ][],
        )}`,
      ),
    register: (input: {
      appointmentId?: string | null;
      patientId?: string | null;
      professionalId?: string | null;
      locationId?: string | null;
      companions?: number;
      accessibilityNeeds?: string | null;
      arrivedAt?: number;
      /** A qué hora tenía el turno, cuando no hay turno cargado que lo diga. */
      scheduledAt?: number | null;
      recordedOffline?: boolean;
    }) =>
      req<{ entry: QueueEntry }>("/waiting-room", {
        method: "POST",
        body: json(input),
      }).then((r) => r.entry),
    transition: (
      id: string,
      input: {
        version: number;
        status: VisitState;
        reason?: string | null;
        exitReason?: string | null;
        occurredAt?: number;
        recordedOffline?: boolean;
      },
    ) =>
      req<{ entry: QueueEntry }>(`/waiting-room/${id}/transition`, {
        method: "POST",
        body: json(input),
      }).then((r) => r.entry),
    setPriority: (
      id: string,
      input: { version: number; priority: VisitPriority; reason: string },
    ) =>
      req<{ entry: QueueEntry }>(`/waiting-room/${id}/priority`, {
        method: "POST",
        body: json(input),
      }).then((r) => r.entry),
    assignRoom: (
      id: string,
      input: { version: number; resourceId: string | null },
    ) =>
      req<{ entry: QueueEntry }>(`/waiting-room/${id}/room`, {
        method: "POST",
        body: json(input),
      }).then((r) => r.entry),
    setFlags: (
      id: string,
      input: {
        version: number;
        pendingDocuments?: boolean;
        pendingPayment?: boolean;
      },
    ) =>
      req<{ entry: QueueEntry }>(`/waiting-room/${id}/flags`, {
        method: "POST",
        body: json(input),
      }).then((r) => r.entry),
    checkinLink: (id: string, version: number) =>
      req<{ url: string; expiresAt: number }>(
        `/waiting-room/${id}/checkin-link`,
        { method: "POST", body: json({ version }) },
      ),
    events: (id: string) =>
      req<{ events: QueueEvent[] }>(`/waiting-room/${id}/events`).then(
        (r) => r.events,
      ),
    /** El aviso de cambios. El tablero sigue sondeando por si esto se cae. */
    streamUrl: () => `${API_URL}/api/waiting-room/stream`,
    /** El check-in de la paciente: sin sesión, el enlace es la credencial. */
    selfCheckin: (token: string) =>
      req<{ alias: string; status: VisitState }>("/public/check-in", {
        method: "POST",
        body: json({ token }),
      }),
  },

  tasks: {
    /** Los filtros se serializan igual acá que en la URL de la pantalla. */
    query: (filters: TaskFilters) => {
      const query = new URLSearchParams({ inbox: filters.inbox });
      if (filters.status?.length) query.set("status", filters.status.join(","));
      if (filters.type?.length) query.set("type", filters.type.join(","));
      if (filters.queue?.length) query.set("queue", filters.queue.join(","));
      if (filters.assignee) query.set("assignee", filters.assignee);
      if (filters.patientId) query.set("patientId", filters.patientId);
      return query;
    },
    list(filters: TaskFilters) {
      return req<{ tasks: TaskRow[] }>(`/tasks?${this.query(filters)}`).then(
        (r) => r.tasks,
      );
    },
    counts: () =>
      req<{ counts: TaskCounts }>("/tasks/counts").then((r) => r.counts),
    detail: (id: string) =>
      req<{ task: ClinicalTask; events: ClinicalTaskEvent[] }>(`/tasks/${id}`),
    create: (input: {
      type: TaskType;
      title: string;
      detail?: string | null;
      patientId?: string | null;
      assigneeId?: string | null;
      queue?: TaskQueue | null;
      priority?: TaskPriority;
      dueAt?: number | null;
    }) =>
      req<{ task: ClinicalTask }>("/tasks", {
        method: "POST",
        body: json(input),
      }).then((r) => r.task),
    update: (
      id: string,
      input: {
        version: number;
        title?: string;
        detail?: string | null;
        priority?: TaskPriority;
        dueAt?: number | null;
      },
    ) =>
      req<{ task: ClinicalTask }>(`/tasks/${id}`, {
        method: "PATCH",
        body: json(input),
      }).then((r) => r.task),
    assign: (
      id: string,
      input: {
        version: number;
        assigneeId?: string | null;
        queue?: TaskQueue | null;
        comment?: string | null;
      },
    ) =>
      req<{ task: ClinicalTask }>(`/tasks/${id}/assign`, {
        method: "POST",
        body: json(input),
      }).then((r) => r.task),
    transition: (
      id: string,
      input: {
        version: number;
        status: TaskStatus;
        resolution?: string | null;
      },
    ) =>
      req<{ task: ClinicalTask }>(`/tasks/${id}/status`, {
        method: "POST",
        body: json(input),
      }).then((r) => r.task),
    comment: (id: string, comment: string) =>
      req<{ events: ClinicalTaskEvent[] }>(`/tasks/${id}/comments`, {
        method: "POST",
        body: json({ comment }),
      }).then((r) => r.events),
    /** La exportación usa el mismo filtro que la pantalla que se está mirando. */
    exportUrl(filters: TaskFilters) {
      return `${API_URL}/api/tasks?${this.query(filters)}&format=csv`;
    },
  },

  timeline: {
    /**
     * Una página de la cronología.
     *
     * Los filtros viajan en la URL —lista separada por comas— para que una
     * vista filtrada se pueda compartir tal cual está.
     */
    list: (
      patientId: string,
      params: {
        categories?: TimelineCategory[];
        cursor?: string;
        limit?: number;
      } = {},
    ) => {
      const query = new URLSearchParams();
      if (params.categories?.length)
        query.set("categories", params.categories.join(","));
      if (params.cursor) query.set("cursor", params.cursor);
      if (params.limit) query.set("limit", String(params.limit));
      const suffix = query.size ? `?${query}` : "";
      return req<TimelinePage>(`/patients/${patientId}/timeline${suffix}`);
    },
    summary: (patientId: string) =>
      req<TimelineSummary>(`/patients/${patientId}/timeline/summary`),
  },
};
